/**
 * Angebots-Builder (Spec 02, F4) — Positions-Editor auf offer_items.
 *
 * Links Event-Kontext, Mitte Optionen A/B/C mit Positionen nach Sektionen, rechts Live-Summen.
 * Preise werden fuer die Anzeige lokal mit derselben Engine (packages/pricing) berechnet
 * (optimistisch); autoritativ ist der Server: Autosave macht PUT /api/offers/:id/items mit
 * If-Match (Optimistic Locking) und uebernimmt die servergerechneten Summen. Geld immer Cents.
 */
import { SprachWahl } from "../ui/auftrag/SprachWahl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { DragEndEvent } from "@dnd-kit/core";
import { computeBreakdown, istPauschal, paketTeile, sortiereVariantenNachPreis, ungedeckteNullpreisIds, type PauschalTeil } from "@maestro/pricing";
// B4 (UX-Polish 25.08.2026): DIE eine Betreff-Quelle. Bis hierher baute `openSend()` unten den
// Betreff selbst und ueberschrieb damit den des Backends — zwei Formulierungen, zwei
// Datumsformate, kein gemeinsamer Pflegeort. Jetzt bildet der Dialog denselben Vorschlag, den
// `buildOfferEmail()` ohnehin gebaut haette; der Override traegt nur noch Handbearbeitung.
import { angebotsBetreff } from "@maestro/angebots-betreff";
import { useApi, apiMutate } from "../hooks/useApi";
import { angebotsseitePfad, BUCHUNGEN_PFAD, vorgangPfad } from "../lib/pfade";
import { useSeitenKontextMelden, vorgangsListe } from "../lib/rueckweg";
import { apiFetch, toApiError, type ApiError } from "../lib/api";
import { openPrintableDocument } from "../lib/print";
import { ladeDateiHerunter } from "../lib/files";
import { leseKompositionsDetails } from "../lib/komposition";
import {
  catalogToItem,
  defaultPriceModeForSection,
  reorderWithinSubset,
  toEngineItems,
  uuid,
  vatLabel,
} from "../lib/builder-helpers";
import type {
  CatalogItem,
  Customer,
  EventRow,
  GooglePlacesLookup,
  Me,
  OfferItem,
  OfferOption,
  OfferOptionDay,
  OfferPriceMode,
  OfferSection,
} from "../ui/types";
import { anlassAnzeige, EVENT_STATUS, formatDate, money } from "../ui/format";
import { AenderungOffenBanner } from "../ui/auftrag/FuerAenderungOeffnen";
import { FehlerOderHinweis } from "../ui/FassungNeuSendenHinweis";
import { Aufklapper, Button, Card, EmptyState, ErrorNote, Icon, ListenReiter, LoadingRows, MenueEintrag, MenueTitel, Modal, PageHeader, StatusPill } from "../ui/components";
import { AufklappZeile, BereitZumSendenKarte, DokumentFuss, useTagesPruefung, gaestezahlPruefung, type Pruefung } from "../ui/PruefSpalte";
// P9.2: dieselbe Stoppuhr-Darstellung wie im Chat-Warte-Erlebnis - keine zweite Formatierung.
import { formatiereWartezeit } from "../ui/komposition-fortschritt";
import { useContentRefetch } from "../ui/CopilotProvider";
// Schritt 2 (Konzept § 4.2): sechs datei-lokal isolierte, reine Prop-Komponenten nach ui/*.tsx.
import { ReferenzAnalyseHinweis } from "../ui/ReferenzAnalyseHinweis";
import { BetriebGeschlossenHinweis } from "../ui/BetriebGeschlossenHinweis";
import { VersionenKarte } from "../ui/VersionenKarte";
import { AngefragteTageKarte } from "../ui/AngefragteTageKarte";
// F-VERSANDSTATUS (02.09.2026, Antoine): "Der Kunde hat die E-Mail bekommen und wie oft hat er
// sie geoeffnet. Und was genau hat er geoeffnet? Die E-Mail selber oder die oeffentliche
// Angebotsseite?" — derselbe Drawer wie in event-detail.tsx (P8), hier zusaetzlich verdrahtet.
// Kein neuer Baustein: `AngebotsVerlaufKarte` beantwortet genau das schon, inkl. dem ehrlichen
// Satz "Wer geoeffnet hat, wird bewusst nicht erfasst" dort, wo es serverseitig keine Daten gibt.
import { AngebotsVerlaufKarte } from "../ui/AngebotsVerlauf";
// Schritt 12 (Konzept § 4.12, Einheit F, letzter Extraktionsschritt): Region 4e, Summen +
// USt-Aufschluesselung + Preis-anpassen-Regler.
import { ZusammenfassungsKarte } from "../ui/ZusammenfassungsKarte";
// Schritt 5 (Konzept § 4.5, Einheit B): erster echter State-Umzug (antwortEntwuerfe/
// antwortBusy/antwortFehler/beantworteteFragen + rueckfrageBeantworten wandern mit).
import { VorDemSendenKarte } from "../ui/VorDemSendenKarte";
// WARTESCHLANGE Punkt 56 (19.09.2026): `ui/SendeReifeKarte.tsx` ist entfallen — Antoine: „Die
// Kachel im Anhang benoetigen wir nicht." An ihrer Stelle steht die Belegung der angefragten Tage.
// Die Signale selbst (`sendeReifeItems` unten) leben unveraendert weiter: sie faerben den CTA,
// nennen die offenen Punkte in der Zeile darunter beim Namen und stehen im Senden-Dialog noch
// einmal vor dem letzten Klick. (Dieselbe Karte war schon am 25.08.2026 einmal entfallen und am
// 28.08. nach Antoines Referenzbildern zurueckgeholt worden — diesmal geht sie auf seinen
// ausdruecklichen Wunsch, und die Stelle bleibt nicht leer.)
// Schritt 7 (Konzept § 4.7, Einheit B): Katalog/KI-Text/Manuell-Tabs des Drawers.
import { PositionHinzufuegenDrawer, type ImportResult } from "../ui/PositionHinzufuegenDrawer";
// Schritt 8 (Konzept § 4.8/§ 6.5, Einheit C): Senden-Dialog (compose/sent/mail_failed).
import {
  A_LA_CARTE_VOR_ORT,
  NACH_KARTE,
  aLaCarteAnsEnde,
  aLaCarteFehlertext,
  istALaCarteOption,
} from "../lib/a-la-carte";
import { AngebotFreischaltenKarte, AngebotGesperrtHinweis, AngenommeneFassungNurLesen, ANGEBOT_GESPERRT_KURZ, istAngebotGesperrt } from "../ui/AngebotGesperrtHinweis";
import { NachtragAusgleichBanner, NachtragLaeuft } from "../ui/NachtragBuilder";
import { currentMembership } from "../lib/membership";
import { AngebotSendenDialog, type SendOutcome, type SendPayload } from "../ui/AngebotSendenDialog";
import { alsUnzustellbar } from "../lib/unzustellbar";
// „Altes Angebot übernehmen“ (Antoine, 29.09.2026): Fenster im Builder statt vorgeschalteter Abfrage.
import { AltesAngebotDrawer, type AltangebotErgebnis } from "../ui/AltesAngebotDrawer";
// Schritt 9 (Konzept § 4.9/§ 2.1, Einheit D): A/B/C-Kacheln + Aktionsreihe + Save-Status.
import { VariantenLeiste } from "../ui/VariantenLeiste";
// Schritt 10 (Konzept § 4.10/§ 2.2, Einheit D): Ein-Reiter-Umschalter + Lese-Tabs, rendert
// OffenePunkteKarte/PaketUebernehmenPanel/VarianteBearbeitenPanel als Kinder.
import { NachschlagenTabs, type Zahlungswege } from "../ui/NachschlagenTabs";
import { LernHinweis, LernSchalter } from "../ui/LernHinweis";
import { VeranstaltungsDatenDrawer } from "../ui/VeranstaltungsDatenDrawer";
import { pruefeRechnungsadresse } from "../lib/adress-pruefung";
import { SpeicherStatus } from "../ui/SpeicherStatus";
import { naechstesVariantenLabel } from "../lib/varianten-label";
// Schritt 11 (Konzept § 4.11/§ 2.7, Einheit E, hoechstes Risiko): DndContext + fuenf Sektionen +
// Positionszeilen (Region 4c). Phase A: reiner Ortswechsel, kein State wandert.
import { MenuewahlKasten, MenuewahlMeldung, istMenuewahlMeldung, menuewahlGruppen } from "../ui/MenuewahlKasten";
import { PositionsListe } from "../ui/PositionsListe";
import type { Kundentyp } from "../ui/Kundentyp";
import type { StornoVorlage } from "../ui/StornoVorlagen";
import { kartenVorschlag, useAbgelehnteVorschlaege } from "../lib/allergen-vorschlag";

/* --------------------------------- Vokabular ------------------------------- */
// Schritt 7 (Konzept § 4.7, Einheit B): exportiert, weil `ui/PositionHinzufuegenDrawer.tsx`
// dieselbe Sektionsliste fuer Reiter/Untertitel braucht (Praezedenzfall: `Pkg`-Export Schritt 2).
export const SECTIONS: { key: OfferSection; label: string; icon: string }[] = [
  { key: "menu", label: "Menü", icon: "restaurant" },
  { key: "drinks", label: "Getränke", icon: "local_bar" },
  { key: "equipment", label: "Equipment", icon: "chair" },
  { key: "staff", label: "Personal", icon: "badge" },
  { key: "other", label: "Sonstiges", icon: "category" },
];
const SEKTIONS_LABEL: Record<string, string> = Object.fromEntries(SECTIONS.map((x) => [x.key, x.label]));

/**
 * B-5 / Entwurf 31c (Handy 390×844): die drei Ebenen am unteren Bildschirmrand.
 *
 * Ganz unten liegt die App-Navigation (`AppShell`, `bottom-0`, ~6rem hoch), darueber die
 * fixierte Summenleiste, darueber der Undo-Toast. Weil beide fixiert sind, nehmen sie im
 * Textfluss KEINEN Platz ein — die letzte Karte der Liste endet sonst unter der Leiste. Der
 * Platzhalter am Listenende ist das einzige, was sie im Fluss vertritt, und er muss darum den
 * ABSTAND der Leiste UND ihre eigene HOEHE abdecken.
 *
 * Bis 11.08.2026 deckte er nur den Abstand (`h-24` = 6rem bei `bottom-24` = 6rem): die Leiste
 * lag in ihrer vollen Hoehe (~4,5rem) auf der letzten Karte, und der Toast (`bottom-40` = 10rem)
 * schnitt ihre Oberkante (10,5rem) an. Beides faellt bei einer Sichtpruefung leicht durch, weil
 * es nur die LETZTE Karte betrifft.
 *
 * Warum benannte Werte statt Zahlen direkt im Klassennamen: die drei haengen rechnerisch
 * zusammen, und genau diese Rechnung ist im Test nachvollziehbar. Die Klassen bleiben trotzdem
 * literal — Tailwind liest den Quelltext und findet zusammengesetzte Namen nicht.
 */
export const MOBIL_UNTEN = {
  /** Abstand der Summenleiste vom Fensterboden — sie steht ueber der App-Navigation. */
  leiste: { klasse: "bottom-24", rem: 6 },
  /** Hoehe der Summenleiste: `p-3` (2 × 0,75rem) + zwei Textzeilen bzw. Knopfhoehe (~3rem). */
  leisteHoeheRem: 4.5,
  /** Platzhalter am Listenende: Abstand + Hoehe, auf die naechste Tailwind-Stufe aufgerundet. */
  platzhalter: { klasse: "h-44", rem: 11 },
  /** Der Undo-Toast liegt UEBER der Leiste, nicht auf ihr. */
  toast: { klasse: "bottom-44", rem: 11 },
} as const;

/** Paket-Zeile von GET /api/packages (siehe pakete.tsx) -- kein `imageUrl`-Feld in echten Daten,
 *  daher Icon-Kachel statt der Foto-Kacheln im Mockup (Design-Abweichung, s. Plan).
 *  Exportiert seit Schritt 2 (Konzept § 4.2): `ui/PackageTile.tsx` importiert den Typ zurueck
 *  (`Pkg` wird auch von `packagesQ`/`applyPackage` hier im Builder gebraucht, kann also nicht
 *  komplett mit der Komponente umziehen). */
export interface Pkg {
  id: string;
  name: string;
  description: string | null;
  kind: string;
  pricePerPersonCents: number;
  /** Pauschalbetrag bei kind === "flat" (Migration 242) - EIN Betrag, nicht je Gast. */
  priceFlatCents?: number;
  vatClass: string;
  archivedAt?: string | null;
  itemCount?: number;
  // LMIV-Aggregat der Gänge (Allergen-Union + „alle gepflegt?"), siehe /api/packages.
  allergenUnion?: string[];
  allergensResolved?: boolean;
  dietaryAll?: Record<string, boolean>;
  // P2 (Speisekarten): Paket referenziert archivierte Gänge (z. B. nach „Karte ersetzen").
  hasArchivedItems?: boolean;
}
/**
 * Der Reiter fuer Positionen OHNE Tageszuordnung (Befund 09.08.2026, Fahrplan § 2.5).
 *
 * Auf einer mehrtaegigen Variante filterte der Builder die Positionsliste hart auf den offenen
 * Tag. Eine Zeile mit `dayNo === null` gehoerte damit zu KEINEM Reiter — sie war in der
 * Oberflaeche nirgends zu sehen und zaehlte trotzdem voll in den Gesamtbetrag (die Engine nimmt
 * fuer sie die Gaestezahl der Variante, `guestsFor()` in `@maestro/pricing`). Gemessen an einer
 * echten Variante (2 Tage à 10/30 Gaeste, Optionsgaestezahl 40, drei Positionen à 10 €/Person):
 * 800,00 € Gesamt, davon 400,00 € auf einer unsichtbaren Zeile.
 *
 * Solche Zeilen entstehen auf zwei Wegen, beide regulaer: `edit_offer_items` des Chats ohne
 * `dayNo`, und `removeDay()` hier, das die Positionen des geloeschten Tages auf `null` setzt.
 * Sie sind nicht per se falsch — eine Pauschale fuer die ganze Veranstaltung gehoert an keinen
 * einzelnen Tag. Falsch war nur, dass man sie nicht sehen konnte.
 *
 * `-1` statt `null`, weil `null` schon „diese Variante hat gar keine Tage" bedeutet (dann ist
 * der Filter aus und ALLES ist sichtbar). Der Wert verlaesst die Oberflaeche nie: alles, was
 * eine `dayNo` schreibt, geht durch `neueZeilenTag` (Schema laesst nur >= 0 zu).
 */
const OHNE_TAG = -1;

export type SaveState = "idle" | "saving" | "saved" | "conflict" | "error";
/** Ergebnis eines Save-Durchlaufs (H-09): Aufrufer (v.a. der Versand) muessen Konflikt/
 *  Fehler ERKENNEN koennen - ein normal aufloesendes Promise ohne Ergebnis reichte nicht. */
type SaveOutcome = "saved" | "conflict" | "error";

/**
 * "Zahlungs-Konditionen"-Karte (Punkt 4, Session 20.08.2026) — Feld-Shape wie
 * apps/api/src/lib/payment-terms.ts PaymentTerms; hier nur die Teilmenge, die die Karte anzeigt.
 * Bank-Felder sind bewusst NICHT hier (die pflegt die Mandanten-Einstellungsseite, s.
 * docs/KONZEPT-ZAHLUNGSKONDITIONEN-PRO-ANGEBOT.md § "Ist-Zustand"). Stornobedingungen WAREN
 * bewusst ausgenommen, sind aber seit 30.08.2026 (Antoine) auch hier je Angebot ueberschreibbar
 * - das Backend-Override-Schema (routes/settings.ts overrideSchema) trug das Feld ohnehin
 * schon mit.
 */
// Exportiert seit Schritt 10 (Konzept § 4.10, Einheit D): `ui/NachschlagenTabs.tsx` zeigt den
// "Zahlungs-Konditionen"-Tab jetzt an (Praezedenzfall: `Pkg`-Export Schritt 2), der State selbst
// bleibt beim Parent (siehe Kopfkommentar dort).
export interface PaymentTermsFields {
  depositPercentBp?: number | null;
  depositAmountCents?: number | null;
  depositMethod?: "none" | "stripe_immediate" | "onsite" | "invoice_advance" | null;
  depositDueDays?: number | null;
  /** Migration 718: worauf sich depositDueDays bezieht (null = nach_bestaetigung). */
  depositDueMode?: "nach_bestaetigung" | "vor_veranstaltung" | null;
  finalPaymentMethod?: "stripe_advance" | "onsite" | "invoice_before_event" | "invoice_after_event" | null;
  finalPaymentDueDaysBeforeEvent?: number | null;
  /** Punkt 116 (Migration 518): Restzahlung N Tage NACH der Veranstaltung (bei invoice_after_event). */
  finalPaymentDueDaysAfterEvent?: number | null;
  /** Punkt 116: ausschliesslich das Zahlungsziel „N Tage nach Rechnungserhalt" (LexOffice). */
  paymentTermDays?: number | null;
  /** Antoine 30.08.2026: je Angebot ueberschreibbar (vorher nur Mandanten-Standard). */
  cancellationTerms?: string | null;
  /** Migration 712: nur in `defaults` belegt — die Vorlagen des Betriebs zur Auswahl am Angebot. */
  stornoVorlagen?: StornoVorlage[] | null;
  /** § 8 (Migration 345, 31.08.2026): Annahme-/Zahlungsfluss-Umschalter, je Angebot
   *  ueberschreibbar wie die uebrigen Felder hier. */
  acceptanceFlow?: "explicit" | "direct_payment" | null;
  paymentPathway?: "stripe" | "kostenuebernahme" | null;
}
export interface PaymentTermsResponse {
  defaults: PaymentTermsFields | null;
  override: PaymentTermsFields;
  effective: PaymentTermsFields;
  summary: string | null;
  /**
   * WARTESCHLANGE Punkt 45: was beim Lesen eines ALTEN Angebots nicht eins zu eins in das neue
   * Modell passt — fertige Sätze vom Server, nicht im Frontend nachgebaut. Sie werden angezeigt,
   * nicht still angewandt: der Betrieb soll sehen, was sich an einem laufenden Vorgang ändert.
   */
  hinweise?: string[] | null;
}

export default function BuilderPage() {
  const { id } = useParams<{ id: string }>();
  const eventQ = useApi<EventRow>(id ? `/api/events/${id}` : null);
  const offersQ = useApi<OfferOption[]>(id ? `/api/events/${id}/offers` : null);
  // Gleicher Datenweg wie Vorgangsseite/Eckdaten: der Verlauf entscheidet, ob die Statuskarte „In Änderung“ nach dem Versand auf Schritt 3 steht.
  const activityQ = useApi<{ action: string; createdAt: string }[]>(id ? `/api/events/${id}/activity` : null);
  const event = eventQ.data;
  // Nachtrag-Version (Einheit 4): nur Inhaber erstellen, senden, ziehen zurück und stimmen telefonisch zu (wie die Server-Routen).
  const meQ = useApi<Me>("/api/me");
  const istInhaber = currentMembership(meQ.data)?.role === "owner";
  // Punkt 115 (G4): wie auf der Vorgangsseite — Liste markieren, Vorgangsname als Titel.
  useSeitenKontextMelden(event ? vorgangsListe(event).to : null, event ? (anlassAnzeige(event.occasion) ?? "Vorgang") : null);
  const customerQ = useApi<Customer>(event ? `/api/customers/${event.customerId}` : null);
  /**
   * R4.2 (§ 4.7): das Warnsignal „dauerhaft geschlossen" am Entscheidungsort. Reiner Lesezugriff
   * auf die eigene Datenbank — das ist der GECACHTE Places-Stand, kein neuer (bezahlter) Abruf.
   * Ohne Places-Zeile bleibt die Antwort `null` und es erscheint nichts.
   */
  const placesQ = useApi<GooglePlacesLookup | null>(event?.customerId ? `/api/customers/${event.customerId}/places-lookup` : null);
  const catalogQ = useApi<CatalogItem[]>("/api/catalog/items");
  // LMIV: Allergene/Diät je Katalog-Position — Positionen mit catalogItemId zeigen die
  // Angaben direkt in der Zeile (Freitext-Positionen haben keine Datenbasis -> „–").
  const lmivByCatalogId = useMemo(
    () =>
      new Map(
        (catalogQ.data ?? []).map((ci) => [ci.id, { allergens: ci.allergens, allergensCheckedAt: ci.allergensCheckedAt, dietary: ci.dietary }]),
      ),
    [catalogQ.data],
  );
  const packagesQ = useApi<Pkg[]>("/api/packages");

  const options = useMemo(
    // Migration 731: eine „À la carte“-Variante (Betrag 0) steht hinter den bepreisten, nicht davor.
    () => aLaCarteAnsEnde(sortiereVariantenNachPreis((offersQ.data ?? []).filter((o) => o.isActive))),
    [offersQ.data],
  );
  /** Nachtrag-Version (sql/764): der offene Entwurf (aktive, nicht gewählte Kopie der angenommenen Variante). */
  const nachtragEntwurf = useMemo(() => options.find((o) => o.nachtragVonOptionId && !o.isChosen) ?? null, [options]);
  const [activeId, setActiveId] = useState<string | null>(null);
  // H-12: welche Variante ist FERTIG GELADEN (Render-Spiegel von loadedIdRef). Editieren und
  // Speichern sind nur erlaubt, wenn loadedOptionId === activeId - waehrend eines Wechsels
  // oder nach einem fehlgeschlagenen loadFull bleibt der Editor gesperrt.
  const [loadedOptionId, setLoadedOptionId] = useState<string | null>(null);
  const [option, setOption] = useState<OfferOption | null>(null);
  const [items, setItems] = useState<OfferItem[]>([]);
  const [days, setDays] = useState<OfferOptionDay[]>([]);
  /**
   * Welcher Tages-Reiter offen ist. `null` = die Variante hat gar keine Tage (alles sichtbar),
   * eine Zahl = dieser Tag, `OHNE_TAG` = der Reiter fuer Positionen ohne Tageszuordnung.
   * Warum es diesen dritten Zustand braucht: siehe `OHNE_TAG`.
   */
  const [activeDay, setActiveDay] = useState<number | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [error, setError] = useState<string | null>(null);
  // Speicherfehler-Untersuchung (24.09.2026, Stephanie Reuter): `error` oben ist die
  // Seiten-Fehlerkarte (`ErrorNote`, u. a. auch fuer `loadFull`-Fehlschlaege) - sie stand bisher
  // ALLEIN dafuer, den wahren Grund eines fehlgeschlagenen Autosaves zu tragen, sass dabei aber
  // unterhalb der Kopfzeile und ging im Zweifel unter (Domenicos Screenshot zeigte nur die
  // Kopfzeile). `saveErrorMessage` ist derselbe Text, zusaetzlich an `SpeicherStatus` durchgereicht
  // -> der `error`-Knopf DORT zeigt ihn jetzt direkt, ohne Scrollen/Hover.
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  /** `true`, wenn der letzte Save mit 409 `event_locked` scheiterte (Versand zeigt dann den Sperrgrund). */
  const letzterSaveGesperrtRef = useRef(false);
  // EIN "Position hinzufügen"-Drawer statt drei verstreuter Einstiege (Katalog/Zeile pro
  // Sektion + globaler "Aus Text"-Button) -- Design Turn 8b, siehe Plan (c).
  // Schritt 7 (Konzept § 2.9/§ 4.7, Einheit B, Phase B): `positionDrawerTab`/`catalogSearch`/
  // `catalogCardFilter`/`importText`/`importBusy`/`importResult` sind vollstaendig nach
  // `ui/PositionHinzufuegenDrawer.tsx` gewandert (reiner Drawer-UI-State). `positionDrawerOpen`/
  // `positionDrawerSection` bleiben hier, weil mehrere Stellen (Sektions-Kopfzeilen,
  // `VariantenLeiste`) den Drawer mit einer bestimmten Sektion oeffnen.
  const [positionDrawerOpen, setPositionDrawerOpen] = useState(false);
  const [positionDrawerSection, setPositionDrawerSection] = useState<OfferSection>("menu");
  /**
   * Rueckbau der Inline-Eckdaten (01.09.2026, Antoine nach dem Ausprobieren: „...es macht doch
   * mehr Sinn, wenn es nicht inline geändert wird, sondern wenn sich das Fenster öffnet..."):
   * `eckdatenOffen` heisst wieder das, was es vor der kurzlebigen Inline-Fassung hiess — „das eine
   * Eckdaten-FENSTER ist offen" (Datum, Gaestezahl, Uhrzeit, Adresse, Gaeste-Variante,
   * Tagesplanung, alles zusammen in `ui/VeranstaltungsDatenDrawer.tsx`). Kein Zwischenzustand pro
   * Feld mehr (kein `datumBearbeiten`/`gaesteBearbeiten`/`uhrzeitBearbeiten`, kein `adresseOffen`
   * fuer einen eigenen Bereich) — das Fenster selbst IST der Bearbeiten-Modus. Bleibt HIER und
   * nicht in der Drawer-Komponente, weil zwei Stellen ihn umschalten koennen (der eine Knopf in
   * der Meta-Zeile UND — unveraendert — derselbe Grund wie bei `positionDrawerOpen`/Schritt 7 und
   * `sendOpen`/Schritt 8).
   */
  const [eckdatenOffen, setEckdatenOffen] = useState(false);
  /** Mockup v3: unter lg zwei Reiter — das Dokument oder „Prüfen & senden". */
  const [mobilReiter, setMobilReiter] = useState<"angebot" | "pruefen">("angebot");
  /** Mockup v3: die zuklappbaren Zeilen „Tag" und „Verlauf" in der Prüfspalte. */
  const [verlaufOffen, setVerlaufOffen] = useState(false);
  /** Sprungziel „Zahlung"/„Gültig bis" im Dokumentfuß → Chip „Zahlungs-Konditionen". */
  const nachschlagenRef = useRef<HTMLDivElement>(null);

  const builderCardsQ = useApi<{ id: string; name: string; active: boolean }[]>("/api/menu-cards");
  const builderCatsQ = useApi<{ id: string; name: string; cardId?: string | null }[]>("/api/catalog/categories");
  const activeBuilderCards = (builderCardsQ.data ?? []).filter((k) => k.active);
  const builderCardByCategory = useMemo(
    () => new Map((builderCatsQ.data ?? []).map((c) => [c.id, c.cardId ?? null])),
    [builderCatsQ.data],
  );
  // Migration 391: `menu_cards.active` jetzt wirklich wirksam - Positionen einer deaktivierten
  // Karte verschwinden aus dem Katalog-Picker (nicht nur aus der Karten-Filter-Auswahl oben).
  const builderCardActiveById = useMemo(() => new Map((builderCardsQ.data ?? []).map((k) => [k.id, k.active])), [builderCardsQ.data]);
  /**
   * Punkt 2 (20.08.2026): applyPackage() legt jetzt eine NEUE Variante an und wechselt per
   * setActiveId() dorthin -- dieselbe "Variante laden"-Effekt-Kette wie bei jedem anderen
   * Varianten-Wechsel (siehe unten) laedt sie danach frisch und raeumt dabei setError(null) auf.
   * Ein direkt in applyPackage() gesetzter Hinweis (Migration 242: Pauschalbetrag im falschen
   * Feld) wuerde also sofort wieder verschwinden. Dieser Ref traegt ihn ueber den Wechsel hinweg
   * bis zu der Stelle in der Effekt-Kette, die ohnehin schon Nach-dem-Laden-Meldungen anzeigt
   * (siehe "flushOutcome" dort) -- fuer die passende optId, damit ein spaeterer, unbeteiligter
   * Wechsel ihn nicht versehentlich mitnimmt.
   */
  const pendingSwitchHinweisRef = useRef<{ optId: string; message: string } | null>(null);
  /**
   * Punkt 2: die neu angelegte Variante existiert serverseitig, bevor `offersQ` (die Liste
   * `/api/events/:id/offers`) sie kennt - `offersQ.reload()` stoesst den Refetch nur AN, `options`
   * (daraus per useMemo abgeleitet) enthaelt die neue ID erst, sobald die Antwort da ist. Ein
   * direktes `setActiveId(neueId)` sofort danach wuerde vom Effekt "Aktive Option initialisieren"
   * (naechster Codeblock) SOFORT wieder zurueckgesetzt: der sieht eine `activeId`, die in `options`
   * (noch) nicht vorkommt, und faellt auf `options[0]` zurueck - bevor die neu geladene Liste
   * ueberhaupt ankommt. Dieser Ref merkt sich stattdessen "sobald diese ID in `options` auftaucht,
   * dorthin wechseln" - der Effekt unten wertet ihn VOR seinem eigenen Fallback aus.
   */
  const pendingActivateIdRef = useRef<string | null>(null);
  /**
   * Bugfund 01.11.2026 (Mimmo, Nachtrag „ich kann nichts ändern“): zu welchem Nachtrag-Entwurf der Builder schon
   * einmal automatisch gewechselt ist. Ein NEU auftauchender Entwurf (Seitenaufruf, gerade erstellt) wird einmal
   * aktiv; danach darf der Betreiber die angenommene Fassung ansehen, ohne zurückgeworfen zu werden.
   */
  const nachtragAutoAktivRef = useRef<string | null>(null);
  /**
   * Bugfund 01.11.2026: darf diese Variante überhaupt gespeichert werden? Im gesperrten Status (angenommen) nur der
   * Nachtrag-Entwurf - wie `darfOptionAendern` auf dem Server. Wird bei jedem Render neu gesetzt (unten, neben
   * `angebotGesperrt`), der Wechsel-Flush liest ihn erst nach dem Render.
   */
  const speicherbarRef = useRef<(optId: string) => boolean>(() => true);
  /**
   * Builder v3 (Entwurf 31a): Welcher der drei Nachschlage-Reiter offen ist — Anfrage im
   * Original, wie MAESTRO sie gelesen hat, Pakete. Default ZU (alle drei werden beim
   * Nachschlagen gebraucht, nicht dauerhaft) und immer nur EINER: Zwei offene Bloecke schoeben
   * die Positionen wieder aus dem Bild, und genau das war Antoines Befund am 03.08.2026.
   */
  const [offenerReiter, setOffenerReiter] = useState<"anfrage" | "verstaendnis" | "offen" | "pakete" | "variante" | "zahlung" | null>(null);
  const nav = useNavigate();
  /** Antoines Regel (31.07.2026): Entwurf loeschen, solange nichts beim Kunden war. */
  const [loeschenOffen, setLoeschenOffen] = useState(false);
  const [loeschBusy, setLoeschBusy] = useState(false);
  // M10: In-Flight-Sperre fuer addOption/duplicateOption - ohne sie loest ein Doppelklick zwei
  // POST-Requests aus, bevor offersQ.reload() das Options-Array aktualisiert hat (doppelte Variante).
  const [busyOption, setBusyOption] = useState(false);
  // M3: Variante loeschen -- ID der zu loeschenden Variante fuer den Bestaetigungs-Dialog (destruktiv).
  const [deleteOptionId, setDeleteOptionId] = useState<string | null>(null);
  /** „Altes Angebot übernehmen“: das Fenster und — nach dem Ersetzen — der Rückgängig-Hinweis. */
  const [altangebotOffen, setAltangebotOffen] = useState(false);
  const [altangebotUndo, setAltangebotUndo] = useState<{ undoId: string; quellNummer: string; optionId: string } | null>(null);
  /**
   * P9.2 (docs/LOOP-ANFRAGEN-WER-IST-DRAN.md, Antoine 30.07.2026): „✨ Mit KI füllen" auf einer
   * LEEREN Variante. `kiFuellenSeit` ist der Startzeitpunkt der laufenden Erzeugung — daraus
   * entsteht die einzige Zahl, die der Knopf waehrend des Wartens zeigt: die TATSAECHLICH
   * verstrichene Zeit. Kein Prozentwert, keine Prognose (dieselbe Haltung wie in
   * `ui/komposition-fortschritt.tsx`, aus dem auch die Formatierung kommt).
   */
  const [kiFuellenSeit, setKiFuellenSeit] = useState<number | null>(null);
  const [kiWartetMs, setKiWartetMs] = useState(0);
  // §5.8 (docs/KONZEPT-ANGEBOTS-BUILDER.md): Tag entfernen nutzt dasselbe Bestaetigungs-Modal-
  // Pattern wie das Varianten-Loeschen (M3), statt sofortiger Ausfuehrung.
  const [deleteDayNo, setDeleteDayNo] = useState<number | null>(null);
  // L4: zuletzt entfernte Position + urspruenglicher Index fuer den Undo-Toast (kein hartes Modal).
  const [removedItem, setRemovedItem] = useState<{ item: OfferItem; index: number } | null>(null);
  const removedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mobile Akkordeon (8e): welche Sektion ist aufgeklappt (nur < lg relevant, ab lg immer alle offen).
  const [openSection, setOpenSection] = useState<OfferSection>("menu");
  const openSectionInitRef = useRef(false);
  // Mockup 34b: Sprungziel fuer die "Öffnen"-Aktion der Rueckfragen-/Wuensche-Zeile -- kein
  // neuer Mechanismus, nur ein Scroll auf die bereits existierende Karte. B1 (25.08.2026): Die
  // Sprungziele haben die entfallene Karte „Bereit zum Senden" ueberlebt; sie haengen jetzt an
  // der Zeile unter dem Senden-CTA.
  const vorDemSendenRef = useRef<HTMLDivElement>(null);
  // Einheitlicher Preis-Regler (8d): Modus + roher Live-Vorschau-Text waehrend des Tippens.
  // Schritt 12 Phase B (Konzept § 3, Einheit F): discountMode/discountPreviewRaw sind nach
  // ui/ZusammenfassungsKarte.tsx gewandert (rein lokaler UI-State dieser Karte).

  /**
   * Builder v3 / B-4 (Entwurf 31a): Welche Abschnitte gerade im Bearbeitungsmodus stehen.
   * Standard ist die ruhige Leseansicht der Vorlage; „Alle bearbeiten" schaltet einen Abschnitt
   * auf die (unveraenderten) Formularzeilen um.
   */
  const [bearbeiteteAbschnitte, setBearbeiteteAbschnitte] = useState<Set<OfferSection>>(new Set());
  const abschnittBearbeiten = (section: OfferSection, an: boolean) =>
    setBearbeiteteAbschnitte((prev) => {
      const next = new Set(prev);
      if (an) next.add(section);
      else next.delete(section);
      return next;
    });

  // Schritt 11 (Phase B, Konzept § 4.11): „EINE Zeile bearbeiten" (B-4, Entwurf 31b) --
  // bearbeiteteZeilen + ihr Ref-Begleiter zeilenZustandRef (der „Abbrechen"-Rueckhalt) sind
  // vollstaendig nach ui/PositionsListe.tsx gewandert (§ 6.2: State und Ref-Begleiter wandern
  // gemeinsam). Keine externe Schreibquelle ausserhalb der Positionsliste -- anders als
  // `bearbeiteteAbschnitte` (bleibt HIER, siehe unten) oder `activeDay`/`offenerReiter`
  // (Schritt 10). `zeileAbbrechen` ruft dort weiterhin `patchItem` -- als Prop hereingereicht,
  // exakt derselbe Weg zum Server wie jede andere Aenderung.

  // Schritt 7 (Phase B): der `tab`-Parameter ist entfallen -- alle drei Aufrufstellen unten
  // uebergaben ohnehin immer "katalog", und `positionDrawerTab` ist jetzt vollstaendig lokaler
  // Zustand von `PositionHinzufuegenDrawer` (setzt sich beim Oeffnen selbst auf "katalog"
  // zurueck, siehe dort).
  /**
   * Mockup v3 (Einfügelinie): wohin die naechste(n) Position(en) aus dem Drawer kommen. `null` =
   * wie bisher ans Ende. Gesetzt: direkt HINTER `nachId`, mit `courseLabel` — und jede weitere
   * Position aus demselben Drawer-Besuch reiht sich dahinter ein (`nachId` wandert mit).
   */
  const einfuegenRef = useRef<{ section: OfferSection; nachId: string; courseLabel: string | null } | null>(null);
  function openPositionDrawer(section: OfferSection, einfuegen?: { nachId: string; courseLabel: string | null }) {
    einfuegenRef.current = einfuegen ? { section, ...einfuegen } : null;
    setPositionDrawerSection(section);
    setPositionDrawerOpen(true);
    // Wer eine Position hinzufuegt, will sie gleich ausfuellen koennen - eine frisch angelegte
    // Zeile in der Leseansicht waere eine leere Zeile ohne Weg hinein.
    abschnittBearbeiten(section, true);
  }
  // Vorschau & Senden (mit KI-Anschreiben) -- 5a (Compose) / 5b (Erfolg) nach Claude-Design-Entwurf.
  // Schritt 8 (Konzept § 4.8/§ 6.5, Einheit C): `AngebotSendenDialog` nach ui/AngebotSendenDialog.tsx.
  // `sendOpen` bleibt HIER (Parent) -- zwei externe Stellen (Desktop-/Mobil-"Senden vorbereiten")
  // oeffnen den Dialog von aussen, exakt dieselbe Begruendung wie `positionDrawerOpen` bei
  // `PositionHinzufuegenDrawer` (Schritt 7). `letter`/`subject`/`letterBusy` bleiben ebenfalls HIER
  // (kontrolliert, als Props durchgereicht) -- `openSend()` befuellt sie synchron beim Oeffnen
  // (KI-Anschreiben nachladen), siehe Kommentar an `openSend()` unten fuer die Begruendung, warum
  // das NICHT in einen `useEffect` in der Komponente verschoben wurde. `sendPhase`/`ccSelf`/
  // `includeOffer`/`freetextText`/`sentResult`/`sendMailError`/`freetextSentAt`/`sending`/`copyDone`
  // sind jetzt reiner Dialog-UI-State (siehe Doku-Kommentar in ui/AngebotSendenDialog.tsx).
  const [sendOpen, setSendOpen] = useState(false);
  const [letter, setLetter] = useState("");
  /** Zuletzt maschinell erzeugter Text (Dialog: von Hand geaendert = weicht davon ab). */
  const [letterErzeugt, setLetterErzeugt] = useState<string | null>(null);
  const [letterBusy, setLetterBusy] = useState(false);
  /** Laeuft gerade ein PDF-Aufbau? Verhindert Doppelklicks und sagt es am Knopf. */
  const [pdfLaedt, setPdfLaedt] = useState(false);
  /** Punkt 7: eigener Ladezustand fuer "Text ueberarbeiten", getrennt von `letterBusy` (Neu
   *  formulieren) - beide Knoepfe sperren sich gegenseitig (kein doppelter Anschreiben-Schreib-
   *  vorgang gleichzeitig), zeigen aber je ihre eigene Beschriftung waehrend des Wartens. */
  const [letterRefineBusy, setLetterRefineBusy] = useState(false);
  /** Antoine 13.10.2026: von Hand bearbeiteter Text, der beim Oeffnen des Sende-Dialogs ersetzt
   *  wurde, weil sich das Angebot geaendert hat — der Dialog bietet ihn zum Wiederherstellen an. */
  const [vorherigesAnschreiben, setVorherigesAnschreiben] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  // D2 (Paket D): zusaetzlich Grussformel und Signatur des Betriebs — der „Kundin fragen"-Drawer
  // setzt sie in seinen Entwurf, statt eine feste Schlusszeile zu erfinden. Kein neuer Fetch:
  // dieselbe Route, die hier ohnehin schon fuer `companyEmail` geladen wird, liefert beide Felder
  // bereits mit (`TENANT_SETTINGS_SICHTBAR`).
  const settingsQ = useApi<{ companyEmail: string | null; emailClosing: string | null; emailSignature: string | null }>(
    "/api/settings/payment-terms",
  );
  // Punkt 4 (20.08.2026): "Zahlungs-Konditionen"-Karte — Override je Vorgang, bestehende Route
  // (GET/PATCH /api/events/:id/payment-terms) hatte bisher keine Builder-UI.
  const paymentTermsQ = useApi<PaymentTermsResponse>(id ? `/api/events/${id}/payment-terms` : null);
  // WARTESCHLANGE Punkt 45: was dieser Betrieb ueberhaupt anbieten kann. Ein Weg, den es nicht
  // gibt, wird gar nicht erst zur Wahl gestellt — statt ihn anzubieten und den Gast spaeter vor
  // einer Sackgasse stehen zu lassen (Ausfall vom 17.09.2026).
  const zahlungswegeQ = useApi<Zahlungswege>("/api/settings/zahlungswege");
  const [ptOverride, setPtOverride] = useState<PaymentTermsFields>({});
  const [ptLoadedFor, setPtLoadedFor] = useState<string | null>(null);
  const [ptSaving, setPtSaving] = useState(false);
  const [ptError, setPtError] = useState<string | null>(null);
  useEffect(() => {
    if (paymentTermsQ.data && ptLoadedFor !== id) {
      setPtOverride(paymentTermsQ.data.override ?? {});
      setPtLoadedFor(id ?? null);
    }
  }, [paymentTermsQ.data, id, ptLoadedFor]);
  /** Schickt IMMER das VOLLSTAENDIGE Override-Objekt — der Server ERSETZT paymentTerms komplett
   *  (kein Merge, settings.ts: `base.paymentTerms = parsed.data`). Ein Teil-Patch wuerde die
   *  uebrigen, bereits gesetzten Override-Felder stillschweigend loeschen. */
  /**
   * Punkt 77, das Wegklicken (21.09.2026): einen offenen Punkt aus der Anfrage ausblenden - oder
   * zurueckholen. Der Vermerk steht am Vorgang, deshalb wird danach der Vorgang neu geladen; die
   * Karte liest ihre Liste aus `event.details` und zeigt das Ergebnis sofort.
   *
   * Ein Fehlschlag laeuft in die bestehende Fehleranzeige der Seite: still zu scheitern waere hier
   * besonders unangenehm - der Betrieb saehe den Punkt verschwinden und beim naechsten Laden
   * wieder da.
   */
  async function offenenPunktAusblenden(wunsch: string, ausgeblendet: boolean) {
    if (!id) return;
    try {
      await apiMutate(`/api/events/${id}/offene-punkte/ausblenden`, "POST", { wunsch, ausgeblendet });
      eventQ.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function savePaymentTerms(patch: Partial<PaymentTermsFields>) {
    if (!id) return;
    // Bugfund 02.11.2026: nach der Zusage sind die Konditionen eingefroren (Server: 409 `event_locked`, routes/settings.ts) -
    // gar nicht erst senden, sondern ruhig sagen, warum.
    if (angebotGesperrt) {
      setPtError("Nach der Zusage des Gastes sind die Bedingungen eingefroren.");
      return;
    }
    const bisher = ptOverride;
    const next = { ...ptOverride, ...patch };
    setPtOverride(next);
    setPtSaving(true);
    setPtError(null);
    try {
      await apiMutate(`/api/events/${id}/payment-terms`, "PATCH", next);
      paymentTermsQ.reload();
    } catch (e) {
      // Nicht gespeichert: den alten Wert zurueck (sonst steht im Feld etwas, das der Server nie
      // bekommen hat) und den Grund sichtbar am Feld zeigen.
      setPtOverride(bisher);
      const status = (e as { statusCode?: number } | null)?.statusCode;
      setPtError(
        status === 409
          ? "Nach der Zusage des Gastes sind die Bedingungen eingefroren."
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setPtSaving(false);
    }
  }
  // K0.6: Antworten + Rueckfragen des Komponisten aus events.details - geprueft, nicht geglaubt.
  const komposition = useMemo(() => leseKompositionsDetails(event?.details), [event?.details]);
  /**
   * §5.12: Gibt es offene Wuensche aus der Anfrage, geht dieser Reiter EINMAL von selbst auf
   * (Konzept: „Default auf, wenn nicht leer").
   *
   * Das verletzt Antoines Layout-Regel vom 03.08.2026 NICHT: die lautet „immer nur EINER offen"
   * (zwei offene Bloecke schoben die Positionen aus dem Bild), und genau einer ist es. Die
   * uebrigen Reiter bleiben zu, weil sie Nachschlagewerke sind — ein offener Punkt aus der
   * Anfrage ist keins. Danach entscheidet der Betrieb: `beimErstenMalGeoeffnet` sorgt dafuer,
   * dass ein zugeklappter Reiter nicht beim naechsten Datenladen wieder aufspringt.
   */
  const beimErstenMalGeoeffnet = useRef(false);
  useEffect(() => {
    if (beimErstenMalGeoeffnet.current) return;
    if (komposition.abdeckung.unmatchedRequests.length === 0) return;
    beimErstenMalGeoeffnet.current = true;
    setOffenerReiter((aktuell) => aktuell ?? "offen");
  }, [komposition.abdeckung.unmatchedRequests.length]);
  // K1.0: Positionen, die der Komponist als Wunsch ohne Katalogposition angelegt hat - kein
  // Katalogbezug, kein Preis. Sie sind die einzige offene Arbeit am Entwurf.
  const offeneWuensche = useMemo(
    () => items.filter((it) => !it.catalogItemId && it.unitPriceCents === 0 && it.isOptional && (it.name ?? "").trim().length > 0),
    [items],
  );
  /**
   * R34-4 (Sende-Reife-Bündel): `offeneRueckfragen.length` wird bewusst NICHT hier ein zweites
   * Mal gefiltert (das wäre ein zweiter Rechenweg) — die Filterung nach bereits-in-dieser-
   * Sitzung-beantworteten Fragen (`beantworteteFragen`) bleibt ausschliesslich lokaler UI-State
   * in `VorDemSendenKarte.tsx` (dortige Doku). Statt die Liste hier zu duplizieren, meldet die
   * Karte ihre eigene, bereits gefilterte Laenge per kleinem Rueckkanal
   * (`onOffeneRueckfragenChange`) nach oben. Initialwert = ungefiltert
   * (`komposition.rueckfragen.length`, korrekt bevor ueberhaupt etwas beantwortet wurde); der
   * Effekt in der Karte korrigiert ihn beim ersten Render sofort, falls doch schon etwas
   * beantwortet war.
   */
  const [offeneRueckfragenCount, setOffeneRueckfragenCount] = useState(() => komposition.rueckfragen.length);
  const letterHydratedRef = useRef(false);

  /**
   * Bughunt 27.08.2026, Punkt 7 (Antoine): "wenn ich den Mailtext anpasse, wird dieser nicht
   * abgespeichert, wenn ich zurueckgehe." Ursache: `letter` war bisher NUR beim tatsaechlichen
   * Versand persistiert (PATCH in sendOffer(), s. unten) - verliess der Betreiber die Seite ohne
   * zu senden, ging jeder Edit verloren, weil der naechste Mount wieder aus
   * `event.details.offerLetter` hydriert (s. Effekt oben) und dort nie etwas Ungeschicktes steht.
   * Debounced Autosave nach demselben Muster wie die Items-Autosave (`dirty`/`touch()` oben):
   * NUR echte Tipp-Edits ueber `handleLetterChange` zaehlen als "dirty" - programmatische
   * `setLetter()`-Aufrufe (Hydration, KI-generieren/-ueberarbeiten) persistieren sich bereits
   * selbst serverseitig und sollen keinen zweiten, ueberfluessigen Save ausloesen.
   */
  const [letterDirty, setLetterDirty] = useState(0);
  /**
   * PR-Review (chatgpt-codex-connector, 27.08.2026): verliess der Betreiber die Seite INNERHALB
   * der 800ms-Debounce-Zeit, verwarf `clearTimeout` im Cleanup den einzigen anstehenden Save -
   * exakt der Datenverlust-Pfad, den dieser Autosave beheben sollte, nur bei "zurueckgehen"
   * statt bei einem Tastendruck-Pause. `letterRef`/`letterPendingRef` sind Refs (nicht Closures),
   * damit ein separater, NUR beim Unmount laufender Effekt (leeres Deps-Array) den zu diesem
   * Zeitpunkt aktuellsten Text noch sicher wegschreiben kann.
   */
  const letterRef = useRef(letter);
  useEffect(() => {
    letterRef.current = letter;
  }, [letter]);
  const letterPendingRef = useRef(false);
  const flushLetterSave = useCallback(() => {
    if (!letterPendingRef.current || !id) return;
    letterPendingRef.current = false;
    void apiMutate(`/api/events/${id}/offer-letter`, "PATCH", { letter: letterRef.current }).catch(() => {
      /* Stiller Autosave-Fehlschlag: der Text bleibt im UI-State sichtbar und wird beim
       * naechsten Edit oder spaetestens beim tatsaechlichen Senden erneut versucht (H-13). */
    });
  }, [id]);
  function handleLetterChange(v: string) {
    setLetter(v);
    letterPendingRef.current = true;
    setLetterDirty((d) => d + 1);
  }
  useEffect(() => {
    if (letterDirty === 0 || !id) return;
    const t = setTimeout(flushLetterSave, 800);
    return () => clearTimeout(t);
    // letter bewusst nicht in den Deps: der Timer soll bei JEDEM Tastendruck neu anlaufen
    // (ueber letterDirty), aber beim Ablaufen den zu dem Zeitpunkt aktuellsten `letter`-Wert
    // aus dem Ref lesen - exakt das Verhalten von setTimeout ueber Renders hinweg.
  }, [letterDirty, id, flushLetterSave]);
  // Unmount-Flush (separat vom Debounce-Effekt oben, s. Kommentar an letterPendingRef): laeuft
  // NUR beim tatsaechlichen Verlassen der Seite, nicht bei jedem letterDirty-Zwischenschritt.
  useEffect(() => {
    return () => flushLetterSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persistiertes Anschreiben EINMAL beim Laden uebernehmen -- verhindert, dass jedes Oeffnen des
  // Senden-Dialogs erneut die KI aufruft, obwohl schon ein (ggf. bearbeitetes) Anschreiben existiert.
  // Neu generiert wird nur noch explizit ueber "Neu vorschlagen".
  // Codex-Befund PR #1892: erst NACH diesem Render steht das gespeicherte Anschreiben im State; `?senden=1` darf
  // openSend() nicht im selben Effekt-Durchlauf starten, sonst ginge `currentLetter: ""` an generate-letter.
  const [letterHydriert, setLetterHydriert] = useState(false);
  useEffect(() => {
    if (letterHydratedRef.current || !event) return;
    letterHydratedRef.current = true;
    setLetterHydriert(true);
    const details = event.details;
    if (details && typeof details === "object" && "offerLetter" in details) {
      const v = (details as Record<string, unknown>).offerLetter;
      if (typeof v === "string" && v.trim()) setLetter(v);
      const g = (details as Record<string, unknown>).offerLetterGenerated;
      if (typeof g === "string" && g.trim()) setLetterErzeugt(g);
    }
  }, [event]);

  // Refs, damit der debounced Autosave immer den frischen Stand + die frische Version sieht.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const daysRef = useRef(days);
  daysRef.current = days;
  // H-11: Save-Zustand pro Options-ID gekapselt. loadedIdRef sagt, WELCHER Variante die
  // aktuellen items/days-Refs gehoeren (nicht zwingend activeId - beim Wechsel laufen beide
  // kurz auseinander). versionsRef fuehrt die If-Match-Version je Variante: ein nach dem
  // Wechsel nachlaufender Save der alten Variante braucht DEREN Version, nicht die der neuen.
  const loadedIdRef = useRef<string | null>(null);
  const versionsRef = useRef(new Map<string, number>());
  const activeIdRef = useRef<string | null>(null);
  activeIdRef.current = activeId;
  // H-10: echte Save-Queue statt savingRef/pendingRef-Flags. chainRef ist das Ende der
  // Save-Kette (laufende + eingereihte Saves), queuedRef der bereits eingereihte, noch
  // nicht gestartete Save. save() loest erst auf, wenn der Stand zum Aufrufzeitpunkt
  // serverseitig bestaetigt (oder endgueltig gescheitert) ist - der Flush vor dem Versand
  // ist damit schlicht `await save()`.
  // H-11: der eingereihte Save traegt sein Ziel (target) - Aufrufer fahren nur mit, wenn
  // ihr Ziel dasselbe ist (nach einem Variantenwechsel wird neu eingereiht statt koalesziert).
  const chainRef = useRef<Promise<SaveOutcome>>(Promise.resolve("saved"));
  const queuedRef = useRef<{ target: string | null; promise: Promise<SaveOutcome> } | null>(null);

  // Aktive Option initialisieren.
  useEffect(() => {
    if (options.length === 0) {
      setActiveId(null);
      pendingActivateIdRef.current = null;
      return;
    }
    // Punkt 2: ein von applyPackage() gewuenschter Wechsel zieht, sobald die neue Variante in
    // `options` angekommen ist - VOR dem Fallback unten, sonst wuerde dieser sie nie erreichen
    // (siehe Kommentar am Ref).
    if (pendingActivateIdRef.current && options.some((o) => o.id === pendingActivateIdRef.current)) {
      if (nachtragEntwurf?.id === pendingActivateIdRef.current) nachtragAutoAktivRef.current = nachtragEntwurf.id;
      setActiveId(pendingActivateIdRef.current);
      pendingActivateIdRef.current = null;
      return;
    }
    // Bugfund 01.11.2026 (Mimmo): mit offenem Nachtrag-Entwurf startet der Builder IM ENTWURF. Bisher fiel er auf die
    // nach Preis erste Variante zurück - oft die angenommene Fassung, und jede Änderung dort endete in 409 `event_locked`.
    if (nachtragEntwurf && nachtragAutoAktivRef.current !== nachtragEntwurf.id) {
      nachtragAutoAktivRef.current = nachtragEntwurf.id;
      if (activeId !== nachtragEntwurf.id) setActiveId(nachtragEntwurf.id);
      return;
    }
    if (!activeId || !options.some((o) => o.id === activeId)) setActiveId(nachtragEntwurf?.id ?? options[0]!.id);
  }, [options, activeId, nachtragEntwurf]);

  // Volle Daten der aktiven Option laden (Positionen + Tage).
  const loadFull = useCallback(async (optId: string) => {
    setError(null);
    try {
      const res = await apiFetch(`/api/offers/${optId}/full`);
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { data: { option: OfferOption; items: OfferItem[]; days: OfferOptionDay[] } };
      // H-11: Antwort nur uebernehmen, wenn die Variante noch die aktive ist - bei schnellem
      // Doppelwechsel darf eine spaet eintreffende Antwort den State der neueren Variante
      // nicht ueberschreiben.
      if (activeIdRef.current !== optId) return;
      setOption(body.data.option);
      setItems(body.data.items);
      setDays(body.data.days);
      versionsRef.current.set(optId, body.data.option.itemsVersion);
      loadedIdRef.current = optId;
      setLoadedOptionId(optId);
      setActiveDay(body.data.days.length > 0 ? body.data.days[0]!.dayNo : null);
      setSaveState("idle");
      setSaveErrorMessage(null);
      // Mobile Akkordeon (8e): erste Sektion mit Positionen aufklappen (einmalig nach dem
      // ersten Laden -- Wechsel der aktiven Option soll die manuelle Nutzerwahl nicht ueberschreiben).
      if (!openSectionInitRef.current) {
        openSectionInitRef.current = true;
        const firstWithItems = SECTIONS.find((s) => body.data.items.some((it) => it.section === s.key));
        setOpenSection(firstWithItems?.key ?? "menu");
      }
    } catch (e) {
      if (activeIdRef.current === optId) setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  // Slice 7 (Live-Sync §5.3): Aendert der Copilot per edit_offer_items die Positionen DIESES
  // Vorgangs, meldet er changed:{resource:"offer", id:eventId}. Dann die Varianten-Leiste (Totals)
  // neu laden und — nur wenn der Editor gerade NICHTS Ungespeichertes haelt — die aktive Variante
  // frisch ziehen. Bei laufendem/kollidierendem Save greift ohnehin der Autosave-Konfliktschutz
  // (409 -> "conflict"-Hinweis mit Neu-laden-Button), also den Editor-State dann bewusst in Ruhe lassen.
  useContentRefetch(
    "offer",
    () => {
      void offersQ.reload();
      if (activeId && (saveState === "idle" || saveState === "saved")) void loadFull(activeId);
    },
    id,
  );

  /* --------------------------------- Autosave -------------------------------- */
  /** Fuehrt EINEN Save-Durchlauf fuer targetId aus (liest itemsRef/daysRef erst beim Start).
   *  Wirft nie. H-11: targetId ist die beim EINREIHEN geladene Variante - haelt der State
   *  inzwischen eine andere (Wechsel dazwischen), wird uebersprungen, denn die Refs enthalten
   *  nicht mehr die Daten von targetId (der Wechsel-Effekt flusht vorher). */
  const runSave = useCallback(async (targetId: string | null): Promise<SaveOutcome> => {
    if (!targetId || loadedIdRef.current !== targetId) return "saved";
    // Bugfund 02.11.2026: eine gesperrte Variante (angenommene Fassung) wird nie gespeichert - der Server lehnt jeden
    // PUT dort mit 409 `event_locked` ab. Erste Sicherung ist `editable` (nichts lässt sich ändern), das hier die zweite.
    if (!speicherbarRef.current(targetId)) return "saved";
    setSaveState("saving");
    try {
      const payloadItems = itemsRef.current.map((it, i) => ({
        id: it.id,
        dayNo: it.dayNo,
        section: it.section,
        courseLabel: it.courseLabel,
        name: it.name.trim() || "Position",
        description: it.description,
        catalogItemId: it.catalogItemId,
        // Migration 384: ANDERS als packageId (bewusst nicht gesendet - Server-Domaene) steuert
        // der Client choiceGroupId aktiv ueber "Aus Wahlgruppe loesen" - muss also mitgeschickt
        // werden, sonst ueberlebt ein Loesen den naechsten Autosave nicht.
        choiceGroupId: it.choiceGroupId,
        qtyMilli: it.qtyMilli,
        unit: it.unit,
        unitPriceCents: it.unitPriceCents,
        catalogPriceCents: it.catalogPriceCents,
        priceMode: it.priceMode,
        vatRateBp: it.vatRateBp,
        isOptional: it.isOptional,
        surcharge: it.surcharge === true,
        guestCount: it.priceMode === "per_person" ? (it.guestCount ?? null) : null,
        // Punkt 32: die Höchstmenge gehört dem Betreiber und muss deshalb mitreisen — ohne sie
        // überlebt ein „höchstens drei Flaschen" den nächsten Autosave nicht.
        addonMaxQty: it.addonMaxQty ?? null,
        // sql/751–753: Art der Zubuchung reist mit, sonst überlebt sie den nächsten Autosave nicht.
        addonErsetztHauptmenue: it.addonErsetztHauptmenue === true,
        addonArt: it.addonArt ?? null,
        addonGruppe: it.addonGruppe ?? null,
        discountable: it.discountable,
        sortOrder: i,
      }));
      const payloadDays = daysRef.current.map((d) => ({ dayNo: d.dayNo, label: d.label, date: d.date, guestCount: d.guestCount }));
      const res = await apiFetch(`/api/offers/${targetId}/items`, {
        method: "PUT",
        headers: { "If-Match": String(versionsRef.current.get(targetId) ?? 0) },
        body: JSON.stringify({ items: payloadItems, days: payloadDays }),
      });
      if (res.status === 409) {
        const conflictBody = (await res.json().catch(() => null)) as { error?: string; message?: string } | null;
        // C-02 / Speicherfehler-Untersuchung (24.09.2026, Stephanie Reuter): `event_locked`
        // (offer-lock.ts) ist KEIN Wettlauf zweier Tabs - "neu laden" behebt hier NICHTS, die
        // Sperre bleibt bestehen. Bisher landete das trotzdem als "conflict" mit der (fuer
        // diesen Fall schlicht falschen) Meldung "wurde inzwischen an anderer Stelle geaendert"
        // - der wahre Grund ("verbindlich angenommen") ging unter. Ein echter
        // version_conflict/cross_option bleibt "conflict" (neu laden hilft dort tatsaechlich).
        if (conflictBody?.error === "event_locked") {
          letzterSaveGesperrtRef.current = true;
          if (loadedIdRef.current === targetId) {
            setSaveErrorMessage(conflictBody.message ?? "Das Angebot wurde verbindlich angenommen - Änderungen sind nur als neue Version möglich.");
            setSaveState("error");
          }
          return "error";
        }
        if (loadedIdRef.current === targetId) setSaveState("conflict");
        return "conflict";
      }
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { data: { option: OfferOption } };
      versionsRef.current.set(targetId, body.data.option.itemsVersion);
      // H-11: UI-Zustand (option/saveState) nur anfassen, solange die Variante noch geladen
      // ist - eine waehrend des Requests gewechselte Ansicht gehoert einer anderen Variante.
      if (loadedIdRef.current === targetId) {
        setOption(body.data.option);
        setSaveState("saved");
        setSaveErrorMessage(null);
      }
      return "saved";
    } catch (e) {
      if (loadedIdRef.current === targetId) {
        // Speicherfehler-Untersuchung (24.09.2026): ein reiner Netzwerkfehler (fetch schlaegt
        // fehl, BEVOR ueberhaupt eine Response existiert) traegt kein `statusCode` - anders als
        // jeder Fehler aus `toApiError()`/`buildHeaders()` (lib/api.ts). Ohne diese Unterscheidung
        // stuende hier oft nur das rohe, englische "Failed to fetch".
        const apiErr = e as Partial<ApiError> | undefined;
        if (apiErr?.code === "event_locked") letzterSaveGesperrtRef.current = true;
        const netzwerkfehler = e instanceof Error && apiErr?.statusCode == null;
        const msg = netzwerkfehler
          ? "Keine Verbindung zum Server – bitte Internetverbindung prüfen und erneut versuchen."
          : e instanceof Error
            ? e.message
            : String(e);
        setError(msg);
        setSaveErrorMessage(msg);
        setSaveState("error");
      }
      return "error";
    }
  }, []);

  /** H-10: reiht einen Save in die Queue ein. Ein bereits EINGEREIHTER (noch nicht
   *  gestarteter) Save liest itemsRef/daysRef erst beim Start und persistiert damit auch
   *  den Stand dieses Aufrufers -> mitfahren statt doppelt einreihen. Laeuft gerade ein
   *  Save, startet der neue erst danach (kein sofort aufloesendes Fake-Promise mehr).
   *  H-11: das Ziel wird beim Einreihen fixiert (die geladene Variante); mitgefahren wird
   *  nur bei GLEICHEM Ziel, sonst wird neu eingereiht. */
  const save = useCallback((): Promise<SaveOutcome> => {
    const targetId = loadedIdRef.current;
    if (queuedRef.current && queuedRef.current.target === targetId) return queuedRef.current.promise;
    const next = chainRef.current.then(() => {
      // Dieser Save startet jetzt: Spaeteres wird NEU eingereiht. Nur den eigenen Eintrag
      // raeumen - ein bereits nachgeruecktes anderes Ziel bleibt eingereiht.
      if (queuedRef.current?.promise === next) queuedRef.current = null;
      return runSave(targetId);
    });
    queuedRef.current = { target: targetId, promise: next };
    chainRef.current = next.catch((): SaveOutcome => "error"); // Kette bleibt defensiv intakt (runSave wirft nie)
    return next;
  }, [runSave]);

  // Variante laden - und vorher ausstehende Saves der NOCH geladenen (alten) Variante
  // abschliessen (H-11): save() ist an loadedIdRef gebunden und die Refs halten bis zum
  // loadFull noch die Daten der alten Variante, der Flush schreibt also garantiert die
  // richtigen Daten an die richtige Options-ID.
  useEffect(() => {
    if (!activeId) return;
    let stale = false;
    void (async () => {
      const previousId = loadedIdRef.current;
      // Bugfund 01.11.2026: eine gesperrte Variante (angenommene Fassung, zurückgezogener Entwurf) wird nicht
      // „geflusht“ - der Server lehnt jeden PUT dort mit 409 `event_locked` ab, und der Fehler stand dann über dem Entwurf.
      const flushOutcome = previousId && previousId !== activeId && speicherbarRef.current(previousId) ? await save() : "saved";
      if (stale) return;
      // H-12: alten Zustand raeumen, BEVOR die neue Variante laedt - schlaegt loadFull fehl,
      // bleiben keine alten Inhalte unter der neuen ID editierbar (loadedOptionId bleibt null,
      // der Editor gesperrt).
      if (previousId !== activeId) {
        loadedIdRef.current = null;
        setLoadedOptionId(null);
        setOption(null);
        setItems([]);
        setDays([]);
        setSaveState("idle");
        setSaveErrorMessage(null);
      }
      await loadFull(activeId);
      // Nach dem Laden melden (loadFull setzt error zurueck): der Flush der vorigen Variante
      // ist gescheitert - deren letzte Aenderungen sind NICHT gespeichert.
      if (!stale && flushOutcome !== "saved") {
        const prevOption = previousId ? options.find((o) => o.id === previousId) : undefined;
        const prevLabel = prevOption ? prevOption.title || (prevOption.aLaCarte ? NACH_KARTE : money(prevOption.amountTotalCents)) : undefined;
        setError(
          flushOutcome === "conflict"
            ? `Die letzten Änderungen an Variante ${prevLabel ?? "?"} wurden NICHT gespeichert (Konflikt – anderweitig geändert). Bitte Variante erneut öffnen und prüfen.`
            : `Die letzten Änderungen an Variante ${prevLabel ?? "?"} konnten NICHT gespeichert werden. Bitte Variante erneut öffnen und prüfen.`,
        );
      } else if (!stale && pendingSwitchHinweisRef.current?.optId === activeId) {
        // applyPackage() (Punkt 2): ein Hinweis aus dem apply-package-Aufruf auf die NEUE
        // Variante, der den Wechsel ueberleben soll - siehe Kommentar am Ref oben.
        setError(pendingSwitchHinweisRef.current.message);
      }
      if (pendingSwitchHinweisRef.current?.optId === activeId) pendingSwitchHinweisRef.current = null;
    })();
    return () => {
      stale = true;
    };
    // options bewusst nicht in den Deps: nur fuer das Fehler-Label gelesen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, loadFull, save]);

  /** H-12: die aktive Variante ist fertig geladen (Anzeige). Ob sie auch BEARBEITET werden darf, sagt `editable` unten. */
  const geladen = loadedOptionId != null && loadedOptionId === activeId;

  /**
   * Darf der GANZE Entwurf weg? Antoines Regel 31.07.2026: nur solange nichts beim Kunden war.
   * EINE Wahrheit fuer beide Wege dorthin - den "Loeschen"-Knopf oben und den Muelleimer an der
   * letzten Variante (02.08.2026, Antoine: "Ich moechte auch eine letzte Variante, so lange sie
   * nicht verschickt wurde, loeschen koennen" + "Warum kann ich genau das nicht einfach mit dem
   * Muelleimer machen?"). Der Server bleibt die Wahrheit (DELETE /api/events/:id lehnt mit
   * 'already_sent' ab) - das hier verhindert nur, dass die Absage ueberhaupt noetig wird.
   */
  const entwurfLoeschbar = Boolean(event && !event.offerSentAt && (event.status === "offer_draft" || event.status === "inquiry"));

  /**
   * B3 (UX-Polish 25.08.2026): Ist der Vorgang nach verbindlicher Annahme gesperrt? Spiegelt
   * `OFFER_LOCKED_STATUSES` aus `apps/api/src/lib/offer-lock.ts` — WORTGLEICH, damit die
   * Oberflaeche dieselbe Grenze zieht wie der Server. Der Server bleibt die Wahrheit (409
   * `event_locked`); diese Liste sorgt nur dafuer, dass der Betreiber die Sperre schon an der
   * gesperrten Checkbox sieht, statt sie sich aus einem Fehler zusammenzureimen.
   */
  const angebotGesperrt = Boolean(event && istAngebotGesperrt(event.status));
  /**
   * Nachtrag-Version: mit offenem Entwurf ist der VERSAND möglich (der Server friert Fassung N+1 aus dem Entwurf ein,
   * `routes/offers.ts`); die angenommene Variante daneben bleibt gesperrt.
   */
  const versandGesperrt = angebotGesperrt && !nachtragEntwurf;
  speicherbarRef.current = (optId: string) => !angebotGesperrt || nachtragEntwurf?.id === optId;
  /**
   * Bugfund 02.11.2026 (Mimmo, Weihnachtsfeier zugesagt, nach #1880): EIN Schalter für die angenommene Fassung.
   * Im gesperrten Status ist jede Variante außer dem Nachtrag-Entwurf nur zum Lesen da - wie `darfOptionAendern` auf
   * dem Server. Ohne Entwurf ist das die angenommene Fassung selbst: Positionen, Titel, Pakete, KI-Füllen, Tage,
   * Preisart … sind dann aus, statt beim Autosave in 409 `event_locked` zu laufen („Nicht gespeichert“ + roter Fehler).
   * Alle Schreibpfade hängen an `editable`, darum steckt `nurLesen` genau dort drin. `runSave` prüft zusätzlich
   * `speicherbarRef` (zweite Sicherung, falls ein künftiger Knopf `editable` vergisst).
   */
  const nurLesen = angebotGesperrt && !(nachtragEntwurf != null && activeId === nachtragEntwurf.id);
  /** H-12 + Sperre: Editieren/Speichern nur, wenn die aktive Variante geladen UND nicht nur zum Lesen ist. */
  const editable = geladen && !nurLesen;

  // Debounced: bei jeder Aenderung (dirty-Zaehler) nach 800 ms speichern.
  const [dirty, setDirty] = useState(0);
  const touch = useCallback(() => setDirty((d) => d + 1), []);
  useEffect(() => {
    if (dirty === 0) return;
    const t = setTimeout(() => void save(), 800);
    return () => clearTimeout(t);
  }, [dirty, save]);

  // Schritt 11 (Phase B, Konzept § 4.11): `dndSensors` (PointerSensor/KeyboardSensor) ist reine,
  // zustandslose dnd-kit-Verkabelung ohne anderen Konsumenten -- vollstaendig nach
  // ui/PositionsListe.tsx gewandert, dort direkt neben dem einzigen `DndContext`, der sie liest.

  /** Reagiert auf ein beendetes Drag (`DndContext.onDragEnd`, umschliesst ALLE Sektionen).
   *  Die Ziel-Sektion (inkl. Tagesfilter) wird erst HIER frisch aus `visibleItems` bestimmt —
   *  ein Drop ausserhalb der eigenen Sektion (Ziel-Id nicht in derselben Teilmenge) wird
   *  ignoriert, siehe `reorderWithinSubset`. */
  function handleDragEnd(event: DragEndEvent) {
    if (!editable) return; // H-12
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeItem = items.find((it) => it.id === active.id);
    if (!activeItem) return;
    const subsetIds = visibleItems.filter((it) => it.section === activeItem.section).map((it) => it.id);
    if (!subsetIds.includes(String(over.id))) return;
    setItems((prev) => reorderWithinSubset(prev, subsetIds, String(active.id), String(over.id)));
    touch();
  }

  /* -------------------------------- Mutationen ------------------------------- */
  function patchItem(itemId: string, patch: Partial<OfferItem>) {
    if (!editable) return; // H-12
    setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, ...patch } : it)));
    touch();
  }
  function removeItem(itemId: string) {
    if (!editable) return; // H-12
    // L4: statt hartem Loeschen die Position samt Index merken und einen Undo-Toast zeigen.
    const idx = items.findIndex((it) => it.id === itemId);
    if (idx === -1) return;
    const removed = items[idx]!;
    setItems((prev) => prev.filter((it) => it.id !== itemId));
    setRemovedItem({ item: removed, index: idx });
    if (removedTimerRef.current) clearTimeout(removedTimerRef.current);
    removedTimerRef.current = setTimeout(() => setRemovedItem(null), 6000);
    touch();
  }
  function undoRemove() {
    if (!editable) return; // H-12
    if (!removedItem) return;
    const { item, index } = removedItem;
    setItems((prev) => {
      const next = [...prev];
      next.splice(Math.min(index, next.length), 0, item);
      return next;
    });
    setRemovedItem(null);
    if (removedTimerRef.current) clearTimeout(removedTimerRef.current);
    touch();
  }
  /**
   * Die `dayNo`, die eine NEU angelegte Zeile bekommt. Im Reiter „Ohne Tag" ist das `null` —
   * `OHNE_TAG` (-1) darf die Oberflaeche nie verlassen (Schema: `dayNo >= 0`).
   */
  const neueZeilenTag = activeDay === OHNE_TAG ? null : activeDay;

  /**
   * Haengt eine neue Position an — oder setzt sie an die Einfuegestelle (Mockup v3), wenn die
   * dort offen ist und zur Sektion passt. Die Reihenfolge im Array IST die Sortierung (`runSave`
   * schreibt `sortOrder: i`), also genuegt das Einsetzen an der richtigen Stelle.
   */
  function einsetzen(prev: OfferItem[], neu: OfferItem): OfferItem[] {
    const ziel = einfuegenRef.current;
    const idx = ziel && ziel.section === neu.section ? prev.findIndex((it) => it.id === ziel.nachId) : -1;
    if (!ziel || idx === -1) return [...prev, neu];
    einfuegenRef.current = { ...ziel, nachId: neu.id };
    return [...prev.slice(0, idx + 1), { ...neu, courseLabel: ziel.courseLabel }, ...prev.slice(idx + 1)];
  }
  function addFreetext(section: OfferSection) {
    if (!editable) return; // H-12
    setItems((prev) =>
      einsetzen(prev, { id: uuid(), optionId: activeId ?? "", dayNo: neueZeilenTag, section, courseLabel: null, name: "", description: null, catalogItemId: null, packageId: null, choiceGroupId: null, qtyMilli: 1000, unit: null, unitPriceCents: 0, catalogPriceCents: null, priceMode: defaultPriceModeForSection(section), vatRateBp: section === "menu" ? 700 : 1900, isOptional: false, discountable: true, sortOrder: prev.length }),
    );
    touch();
  }
  function addFromCatalog(ci: CatalogItem, qty = 1) {
    if (!editable) return; // H-12
    setItems((prev) => einsetzen(prev, { ...catalogToItem(ci, neueZeilenTag, prev.length, Math.round(qty * 1000)), optionId: activeId ?? "" }));
    touch();
  }

  /**
   * Schnellstart (8a): Paket in eine NEUE Variante uebernehmen.
   *
   * Punkt 2 (Antoine 20.08.2026, Screenshot „Paket übernehmen"-Panel): frueher schrieb dieselbe
   * Aktion additiv in die AKTIVE Variante - fachlich ein Ueberschreiben, weil die gerade
   * bearbeitete Variante dadurch veraendert wurde, ohne dass man das ausdruecklich wollte. Jetzt
   * entsteht eine NEUE Variante - GENAU derselbe Weg wie addOption() ("Variante hinzufügen"):
   * POST /api/events/:id/offers legt eine leere Variante an (ohne Obergrenze - seit
   * 12.10.2026 sind beliebig viele Varianten erlaubt), danach fuellt apply-package sie mit den Paket-Positionen.
   * Die bisher aktive Variante bleibt komplett unangetastet; die neue wird aktiv.
   *
   * Ergaenzung (Antoine 20.10.2026, WvonZ-Durchtest): Hat die AKTIVE Variante keine einzige Position
   * (ueber alle Abschnitte und Tage), laeuft EIN Paket stattdessen in genau diese leere Variante
   * (`applyPackageInAktive`, derselbe Weg wie „in die offene Variante“) - sonst bliebe eine leere
   * Variante A neben der befuellten Variante B stehen. Das gilt fuer alle Einstiege von
   * `applyPackage` (Panel, Schnellstart, Katalog-Chip). Bei Mehrfachauswahl (mehrere Pakete
   * auf einmal) bleibt es bei je einer neuen Variante je Paket. Hat die aktive Variante
   * Positionen: unveraendert neue Variante.
   */
  // Antoine 12.10.2026: mehrere passende Pakete (z. B. Silvester mit und ohne Weinbegleitung)
  // auf einmal - je Paket eine eigene neue Variante, der Name folgt auf dem Server dem Paket
  // (paketAnwenden). Labels werden hier lokal fortgezaehlt, weil `options` erst nach reload()
  // die neuen Varianten kennt. Die zuletzt angelegte Variante wird aktiv.
  async function applyPackage(auswahl: Pkg | Pkg[]) {
    if (!id || !editable) return; // H-12
    const pakete = Array.isArray(auswahl) ? auswahl : [auswahl];
    if (pakete.length === 0) return;
    // Bugfund 01.11.2026 (Mimmo, Weihnachtsfeier zugesagt): im gesperrten Status gibt es keine neue Variante (Server:
    // 409 `event_locked`). Im Nachtrag kommen die Pakete deshalb in den Entwurf - additiv, wie „in die offene Variante“.
    if (angebotGesperrt) {
      if (!nachtragEntwurf || activeId !== nachtragEntwurf.id) {
        setError(
          nachtragEntwurf
            ? "Im Nachtrag kommen Pakete in den Entwurf. Bitte zuerst die Karte „Nachtrag (Entwurf)“ auswählen."
            : "Das Angebot ist angenommen. Erstelle zuerst einen Nachtrag, dann kannst du ein Paket übernehmen.",
        );
        return;
      }
      await applyPaketeInAktive(pakete);
      return;
    }
    // 20.10.2026: leere, fertig geladene aktive Variante -> das (einzelne) Paket dort hinein.
    if (pakete.length === 1 && activeId && loadedOptionId === activeId && items.length === 0) {
      await applyPackageInAktive(pakete[0]!);
      return;
    }
    setError(null);
    const vergeben = options.map((o) => o.label);
    let zuletzt: string | null = null;
    const hinweise: string[] = [];
    try {
      for (const pkg of pakete) {
        const label = naechstesVariantenLabel(vergeben);
        vergeben.push(label);
        const created = await apiMutate<{ id: string }>(`/api/events/${id}/offers`, "POST", { label, guestCount: event?.guestCount ?? 1 });
        zuletzt = created.id;
        // Die neue Variante hat noch keine Tage - dayNo darf hier nur auf existierende Tage der
        // Zieloption zeigen (Server-Validierung), also immer null ("Ohne Tag").
        const res = await apiMutate<{ added: number; skipped: number; hinweis?: string | null }>(`/api/offers/${created.id}/apply-package/${pkg.id}`, "POST", { dayNo: null });
        // §5.2 (docs/KONZEPT-ANGEBOTS-BUILDER.md): apply-package ist idempotent (Server ueberspringt
        // bereits vorhandene Katalog-Positionen) - bei einer frisch angelegten, leeren Variante kann
        // "skipped > 0" praktisch nicht vorkommen, die Meldung bleibt als Absicherung stehen.
        const h =
          res.added === 0 && res.skipped > 0
            ? `Alle Positionen aus „${pkg.name}" sind bereits in dieser Variante vorhanden - nichts wurde hinzugefügt.`
            : // Migration 242: benannter Datenbefund (Pauschalbetrag im Feld „Preis pro Gast").
              (res.hinweis ?? null);
        if (h) hinweise.push(pakete.length > 1 ? `${pkg.name}: ${h}` : h);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    if (!zuletzt) return;
    // KEIN direktes setActiveId hier: `options` kennt die neue Variante erst nach dem Refetch.
    // pendingActivateIdRef wird vom Effekt "Aktive Option initialisieren" konsumiert, sobald sie
    // in `options` auftaucht. REIHENFOLGE WICHTIG: beide Refs VOR offersQ.reload() setzen (sonst
    // greift der Fallback auf options[0], siehe Kommentar dort).
    if (hinweise.length) pendingSwitchHinweisRef.current = { optId: zuletzt, message: hinweise.join(" ") };
    pendingActivateIdRef.current = zuletzt;
    offersQ.reload();
  }

  /**
   * Punkt C (Antoine 13.10.2026, „Nachfragen"): ein Paket aus dem Katalog-Picker wahlweise in die
   * OFFENE Variante uebernehmen. Erst die lokalen Aenderungen speichern (sonst wuerde loadFull sie
   * verwerfen), dann dieselbe Server-Route wie applyPackage() auf die aktive Variante - Betraege
   * rechnet der Server, der Editor laedt danach den Serverstand neu.
   */
  async function applyPackageInAktive(pkg: Pkg) {
    await applyPaketeInAktive([pkg]);
  }
  /**
   * Codex-Befund PR #1880: mehrere Pakete in die aktive Variante (Nachtrag) - EINMAL speichern, alle Pakete serverseitig
   * anwenden, EINMAL neu laden. Je Paket „speichern + neu laden“ konnte vor dem nächsten Render die alten Positionen mit
   * der neuen Version speichern und so das vorige Paket still entfernen (PUT /items löscht Ausgelassenes).
   */
  async function applyPaketeInAktive(pakete: Pkg[]) {
    if (!id || !editable || !activeId || pakete.length === 0) return; // H-12
    const optId = activeId;
    setError(null);
    if ((await save()) !== "saved") return; // runSave meldet den Fehler selbst
    const hinweise: string[] = [];
    let fehler: string | null = null;
    for (const pkg of pakete) {
      try {
        const res = await apiMutate<{ added: number; skipped: number; hinweis?: string | null }>(`/api/offers/${optId}/apply-package/${pkg.id}`, "POST", { dayNo: null });
        const h =
          res.added === 0 && res.skipped > 0
            ? `Alle Positionen aus „${pkg.name}" sind bereits in dieser Variante vorhanden - nichts wurde hinzugefügt.`
            : (res.hinweis ?? null);
        if (h) hinweise.push(pakete.length > 1 ? `${pkg.name}: ${h}` : h);
      } catch (e) {
        fehler = e instanceof Error ? e.message : String(e);
        break;
      }
    }
    await loadFull(optId);
    offersQ.reload();
    // nach loadFull, das error zuruecksetzt
    if (fehler) setError(fehler);
    else if (hinweise.length) setError(hinweise.join(" "));
  }

  /**
   * „Altes Angebot übernehmen“ (Antoine, 29.09.2026) — der Server hat geschrieben:
   *  - `neu`: eine neue Variante; wie `applyPackage()` über `pendingActivateIdRef` zu ihr wechseln,
   *    sobald `offersQ.reload()` sie kennt.
   *  - `ersetzen`: die aktuelle Variante neu laden (Positionen, Summen, itemsVersion) und das
   *    Rückgängig anbieten — über den Undo-Stack (`POST /api/undo/:id`), nicht lokal.
   */
  async function altangebotUebernommen(e: AltangebotErgebnis) {
    setAltangebotOffen(false);
    setError(null);
    if (e.modus === "neu") {
      pendingActivateIdRef.current = e.option.id;
      offersQ.reload();
      return;
    }
    await loadFull(e.option.id);
    offersQ.reload();
    if (e.undoId) setAltangebotUndo({ undoId: e.undoId, quellNummer: e.quellNummer, optionId: e.option.id });
  }
  async function altangebotRueckgaengig() {
    if (!altangebotUndo) return;
    const { undoId, optionId } = altangebotUndo;
    setAltangebotUndo(null);
    try {
      await apiMutate(`/api/undo/${undoId}`, "POST");
      if (activeIdRef.current === optionId) await loadFull(optionId);
      offersQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }
  useEffect(() => {
    if (!altangebotUndo) return;
    const t = setTimeout(() => setAltangebotUndo(null), 15000);
    return () => clearTimeout(t);
  }, [altangebotUndo]);

  /**
   * KI-Freitext-Import: Text an /parse-freeform, Entwurf als Positionen uebernehmen (kein
   * Auto-Commit). Schritt 7 (Phase B, Konzept § 2.9): `importBusy`/`importResult` sind jetzt
   * lokaler Zustand von `PositionHinzufuegenDrawer` (per `onParseFreeform`-Prop, analog zu
   * `applyPackage`/`onApply` aus Schritt 6) -- diese Funktion selbst bleibt hier, weil sie in
   * `items` (fachlicher State, Konzept § 3 Tabelle Zeile 1) schreibt und `touch()` die
   * Autosave-Kette anstoesst. Das H-12-Gate bleibt darum unveraendert am Funktionskopf (Konzept
   * § 6.4): Text kommt jetzt als Parameter statt aus der frueheren `importText`-Closure, das
   * Ergebnis wird zurueckgegeben statt in `importResult` geschrieben -- der Aufrufer (die
   * Drawer-Komponente) setzt ihren eigenen lokalen State daraus.
   */
  async function parseFreeform(text: string): Promise<ImportResult | null> {
    if (!id || !text.trim() || !editable) return null; // H-12
    setError(null);
    try {
      const res = await apiFetch(`/api/events/${id}/offers/parse-freeform`, { method: "POST", body: JSON.stringify({ text }) });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as {
        data: { items: { name: string; section: OfferSection; priceMode: OfferPriceMode; qtyMilli: number; unit: string | null; unitPriceCents: number; vatRateBp: number; description: string | null; catalogItemId?: string | null }[]; computedTotalCents: number | null; statedTotalCents: number | null; flags: { message: string }[] };
        warning?: string;
      };
      const parsed = body.data.items.map((it, i): OfferItem => ({
        id: uuid(), optionId: activeId ?? "", dayNo: neueZeilenTag, section: it.section, courseLabel: null, name: it.name,
        // catalogItemId: eindeutiger Namenstreffer im Katalog (Server, `@maestro/katalog-treffer`) —
        // nur fuer die Allergene aus der Karte, der Preis bleibt der aus dem Text.
        description: it.description, catalogItemId: it.catalogItemId ?? null, packageId: null, choiceGroupId: null, qtyMilli: it.qtyMilli, unit: it.unit, unitPriceCents: it.unitPriceCents,
        catalogPriceCents: null, priceMode: it.priceMode, vatRateBp: it.vatRateBp, isOptional: false, discountable: true, sortOrder: items.length + i,
      }));
      setItems((prev) => parsed.reduce(einsetzen, prev));
      if (parsed.length > 0) touch();
      return { count: parsed.length, computedTotalCents: body.data.computedTotalCents, statedTotalCents: body.data.statedTotalCents, flags: body.data.flags, warning: body.warning ?? null };
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    }
  }

  /**
   * P9.2: die Stoppuhr laeuft nur, solange wirklich gewartet wird - eine Sekunde Takt reicht,
   * die Anzeige ist auf Sekunden genau. Kein Intervall im Leerlauf.
   */
  useEffect(() => {
    if (kiFuellenSeit == null) return;
    setKiWartetMs(Date.now() - kiFuellenSeit);
    const iv = setInterval(() => setKiWartetMs(Date.now() - kiFuellenSeit), 1000);
    return () => clearInterval(iv);
  }, [kiFuellenSeit]);

  async function addOption() {
    if (!id || busyOption) return;
    setBusyOption(true);
    // Nur noch ein Startwert fuer die DB-Spalte, fachlich bedeutungslos: es gibt seit dem
    // 11.09.2026 (Antoine) ueberhaupt keine Buchstaben-Kennung mehr in der Anzeige - Titel oder
    // Preis identifizieren die Variante.
    const label = naechstesVariantenLabel(options.map((o) => o.label));
    try {
      // G3: amountTotalCents wird nicht mehr mitgeschickt - der Server startet jede Variante
      // bei 0, Summen schreibt ausschliesslich die Pricing-Engine.
      await apiMutate(`/api/events/${id}/offers`, "POST", { label, guestCount: event?.guestCount ?? 1 });
      offersQ.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyOption(false);
    }
  }
  /**
   * P9.2 — „✨ Mit KI füllen" auf einer LEEREN Variante.
   *
   * Ruft `POST /api/offers/:id/mit-ki-fuellen` auf. Die Erzeugung selbst ist DIESELBE wie beim
   * Weg Anfrage -> Angebot (apps/api/src/lib/kompositions-lauf.ts); hier passiert nichts weiter,
   * als sie anzustossen und danach die Variante neu zu laden.
   *
   * WARTEZEIT: gemessene 70-91 Sekunden je Komposition (docs/LOOP-KI-AGENT.md K0.7/K1.5). Der
   * Knopf zeigt deshalb waehrenddessen die verstrichene Zeit samt dieser Spanne - eine Sekunde
   * ohne Rueckmeldung ist bei anderthalb Minuten nicht von „haengt" zu unterscheiden.
   *
   * Der ECHTE Serverfortschritt (drei Schritte) haengt an `inquiries.generation_phase` und ist
   * damit an eine Anfrage gebunden; stammt der Vorgang aus einer, meldet die Route ihn dorthin
   * (dieselbe Quelle wie Anfragenseite und Chat-Karte). Ein zweites Fortschrittsfeld nur fuer den
   * Builder waere ein zweiter Anzeigeweg - siehe Kopf der Route.
   */
  async function varianteMitKiFuellen() {
    if (!activeId || !editable || busyOption || kiFuellenSeit != null) return;
    setKiFuellenSeit(Date.now());
    setKiWartetMs(0);
    setError(null);
    try {
      const res = await apiFetch(`/api/offers/${activeId}/mit-ki-fuellen`, { method: "POST" });
      if (!res.ok) throw await toApiError(res);
      // Neu laden statt die Antwort in den State zu schieben: die Route hat serverseitig
      // geschrieben (Positionen, Summen, itemsVersion) - `loadFull` holt genau diesen Stand,
      // inklusive der If-Match-Version, mit der der Editor weiterarbeitet.
      await loadFull(activeId);
      offersQ.reload();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(
        /variante_nicht_leer/i.test(msg)
          ? "Diese Variante enthält bereits Positionen – „Mit KI füllen“ geht nur bei einer leeren Variante, sonst wäre die bisherige Arbeit weg."
          : msg,
      );
    } finally {
      setKiFuellenSeit(null);
    }
  }

  async function duplicateOption() {
    if (!activeId || busyOption) return;
    setBusyOption(true);
    try {
      const res = await apiFetch(`/api/offers/${activeId}/duplicate`, { method: "POST" });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { data: { id: string } };
      offersQ.reload();
      setActiveId(body.data.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyOption(false);
    }
  }

  /** M3: Variante loeschen -- Soft-Delete via isActive:false (options wird ohnehin auf isActive
   *  gefiltert). Seit 12.10.2026 auch die letzte (nur unversendeter Entwurf, Server-Guard). */
  /**
   * Vorgang loeschen - nur solange nichts beim Kunden war (Antoines Regel 31.07.2026).
   *
   * Der SERVER ist die Wahrheit: Er lehnt mit 'already_sent' ab, sobald ein Angebot hinausgegangen
   * ist. Die Oberflaeche versteckt den Knopf zusaetzlich, damit die Absage gar nicht erst noetig
   * wird - aber sie ERSETZT die Serverpruefung nicht. Ein Knopf, der nur versteckt ist, ist kein
   * Schutz.
   */
  async function vorgangLoeschen(): Promise<void> {
    if (!id) return;
    setLoeschBusy(true);
    setError(null);
    try {
      await apiMutate(`/api/events/${id}`, "DELETE");
      nav(event ? vorgangsListe(event).to : BUCHUNGEN_PFAD);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(
        /already_sent|bereits.*versendet/i.test(msg)
          ? 'Dieser Vorgang wurde bereits an den Kunden versendet und kann nicht mehr geloescht werden. Nutze "Stornieren" auf der Vorgangsseite - das beendet den Vorgang und behaelt ihn als Beleg.'
          : msg,
      );
      setLoeschenOffen(false);
    } finally {
      setLoeschBusy(false);
    }
  }

  async function deleteOption(optId: string) {
    // Auch die LETZTE Variante (Antoine 12.10.2026) - der Server laesst das nur im unversendeten
    // Entwurf zu; der Builder faellt danach in den leeren Zustand („Variante anlegen").
    if (busyOption) return;
    setBusyOption(true);
    setError(null);
    try {
      await apiMutate(`/api/offers/${optId}`, "PATCH", { isActive: false });
      // War die aktive Variante betroffen, waehlt der Init-Effekt nach reload die erste uebrige.
      if (activeId === optId) setActiveId(null);
      offersQ.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyOption(false);
      setDeleteOptionId(null);
    }
  }

  async function setDiscount(percentBp: number | null, amountCents: number | null) {
    if (!activeId || !editable) return; // H-12
    try {
      const res = await apiFetch(`/api/offers/${activeId}/discount`, { method: "POST", body: JSON.stringify({ discountPercentBp: percentBp, discountAmountCents: amountCents }) });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { data: { option: OfferOption } };
      versionsRef.current.set(activeId, body.data.option.itemsVersion);
      setOption(body.data.option);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }
  async function setTargetPrice(targetCents: number) {
    if (!activeId || !editable) return; // H-12
    try {
      const res = await apiFetch(`/api/offers/${activeId}/set-target-price`, { method: "POST", body: JSON.stringify({ targetCents }) });
      if (!res.ok) throw await toApiError(res);
      await loadFull(activeId); // Anpassungszeile neu einlesen
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  /**
   * KONZEPT-GESAMTPREIS-PRO-MENUE: Umschalter Pro-Gang <-> Gesamtpreis. PATCHt die Option, die
   * Route loest serverseitig recompute() aus (offers.ts) - die Antwort traegt bereits die neu
   * gerechneten Summen, deshalb ersetzt sie `option` vollstaendig statt nur das eine Feld zu
   * setzen (anders als beim Titel/Gaeste-Feld, das lokal weiterschreibt und separat speichert).
   */
  async function setPricingMode(mode: "per_course" | "total" | "total_event") {
    if (!activeId || !editable || !option || option.pricingMode === mode) return; // H-12
    const zuvor = option;
    setOption({ ...option, pricingMode: mode }); // optimistisch, fuer die sofortige Live-Vorschau
    try {
      const updated = await apiMutate<OfferOption>(`/api/offers/${activeId}`, "PATCH", { pricingMode: mode });
      setOption(updated);
    } catch (e) {
      setOption(zuvor);
      setError(e instanceof Error ? e.message : String(e));
    }
  }
  /** KONZEPT-GESAMTPREIS-PRO-MENUE: Menüpreis pro Person (Brutto-Cent), nur im 'total'-Modus gelesen. */
  async function setMenuTotalPrice(cents: number | null) {
    if (!activeId || !editable || !option) return; // H-12
    try {
      const updated = await apiMutate<OfferOption>(`/api/offers/${activeId}`, "PATCH", { menuTotalPriceCents: cents });
      setOption(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  /**
   * Bughunt 27.08.2026: dieselbe Umschalt-/Gesamtpreis-Logik wie setPricingMode/setMenuTotalPrice,
   * verallgemeinert auf alle Sektionen AUSSER Menue (das bleibt auf PATCH .../offers/:id). Eine
   * Funktion statt zwei (kein Umschalter/Betrag getrennt) — der neue Endpunkt nimmt beides
   * zusammen entgegen, es gibt hier keinen Bestandscode, den ein Split wiederverwenden koennte.
   */
  async function setSectionPricing(section: string, mode: "per_course" | "total" | "total_event", totalPricePerPersonCents: number | null) {
    if (!activeId || !editable || !option) return; // H-12
    const zuvor = option;
    setOption({ ...option, sectionPricing: { ...option.sectionPricing, [section]: { mode, totalPricePerPersonCents } } }); // optimistisch
    try {
      const body = await apiMutate<{ option: OfferOption }>(`/api/offers/${activeId}/section-pricing`, "POST", { section, pricingMode: mode, totalPricePerPersonCents });
      setOption(body.option);
    } catch (e) {
      setOption(zuvor);
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function enableDays() {
    if (!editable || days.length > 0) return; // H-12
    const d0: OfferOptionDay = { id: uuid(), optionId: activeId ?? "", dayNo: 0, label: "Tag 1", date: null, guestCount: option?.guestCount ?? event?.guestCount ?? 0 };
    setDays([d0]);
    setActiveDay(0);
    setItems((prev) => prev.map((it) => ({ ...it, dayNo: 0 })));
    touch();
  }
  function addDay() {
    if (!editable) return; // H-12
    const nextNo = days.length === 0 ? 0 : Math.max(...days.map((d) => d.dayNo)) + 1;
    setDays((prev) => [...prev, { id: uuid(), optionId: activeId ?? "", dayNo: nextNo, label: `Tag ${nextNo + 1}`, date: null, guestCount: option?.guestCount ?? 0 }]);
    setActiveDay(nextNo);
    touch();
  }
  function patchDay(dayNo: number, patch: Partial<OfferOptionDay>) {
    if (!editable) return; // H-12
    setDays((prev) => prev.map((d) => (d.dayNo === dayNo ? { ...d, ...patch } : d)));
    touch();
  }
  function removeDay(dayNo: number) {
    if (!editable) return; // H-12
    const restTage = days.filter((d) => d.dayNo !== dayNo);
    setDays(restTage);
    setItems((prev) => prev.map((it) => (it.dayNo === dayNo ? { ...it, dayNo: null } : it)));
    // Die Positionen des geloeschten Tages haben jetzt keine Tageszuordnung mehr. Bleiben andere
    // Tage stehen, muss der Reiter „Ohne Tag" sie auffangen — vorher landete `activeDay` auf
    // `null`, was den Tagesfilter ganz abschaltete und ALLE Tage vermischt anzeigte.
    setActiveDay((prev) => (prev === dayNo ? (restTage.length > 0 ? OHNE_TAG : null) : prev));
    touch();
  }

  /** Vorschau öffnen und das KI-Anschreiben erzeugen, falls noch keins da ist ODER sich der
   *  Angebotsinhalt seit dem Erzeugen geaendert hat (Fingerabdruck, Antoine 13.10.2026).
   *  Schritt 8: `sendPhase` selbst ist jetzt lokaler State von `AngebotSendenDialog` -- die
   *  Komponente setzt ihn per `useLayoutEffect` bei jedem Uebergang `open: false -> true` auf
   *  "compose" zurueck (reproduziert das bisherige `setSendPhase("compose")` hier). Diese
   *  Funktion bleibt bewusst unveraendert im Parent (nicht in einen `useEffect` der Komponente
   *  verschoben): sie laedt beim Oeffnen synchron das KI-Anschreiben nach, ein Umbau auf ein
   *  Effekt-Muster war fuer diesen geld-/versandnahen Schritt nicht mit Sicherheit als
   *  verhaltensgleich zu belegen (Konzept § 6.5). */
  async function openSend() {
    setError(null);
    setSendOpen(true);
    /**
     * B4: derselbe Aufruf, den `buildOfferEmail()` serverseitig macht — gleiche Funktion, gleiche
     * Eingaben. Die Gaestezahlen kommen aus den AKTIVEN Varianten (§5.19, die Zahlen, auf die der
     * Preis gerechnet ist), `event.guestCount` ist nur der Rueckfall. Damit sieht der Betreiber
     * im Dialog exakt den Betreff, der ohne sein Zutun hinausginge — und `overrideSubject`
     * transportiert ab jetzt nur noch, was er von Hand daran geaendert hat.
     */
    // Nachtrag: immer der feste Betreff (Antoine, 30.10.2026) und kein KI-Anschreiben - der Server schickt zu einem Nachtrag keins.
    if (nachtragEntwurf) {
      setSubject("Nachtrag zu Ihrem Auftrag");
      setLetter("");
      setVorherigesAnschreiben(null);
      return;
    }
    if (!subject && event) {
      setSubject(
        angebotsBetreff({
          lang: event.offerLanguage ?? "de",
          eventDate: event.eventDate,
          // `options` ist bereits auf die AKTIVEN gefiltert (s. oben) — dieselbe Menge, die der
          // Server beim Versand als versendbares Angebot zusammenstellt.
          optionGuestCounts: options.map((o) => o.guestCount),
          guestCount: event.guestCount,
          // Buchungsstrecke A3: derselbe Anlass wie im Backend-Betreff (`buildOfferEmail`).
          occasion: event.occasion,
          // B3: derselbe Name wie im Backend-Betreff (`customers.name`).
          kundenName: customerQ.data?.name,
        }),
      );
    }
    if (!id) return;
    setVorherigesAnschreiben(null);
    setLetterBusy(true);
    try {
      // Antoine 13.10.2026: bei jedem Oeffnen prueft der Server per Fingerabdruck, ob das
      // Anschreiben noch zum Angebotsinhalt passt, und erzeugt es nur dann neu. Laufende Saves
      // der Positionen vorher abwarten, damit er den aktuellen Stand vergleicht.
      // ponytail: ein noch nicht gestarteter Debounce-Save (<800 ms) wird nicht erzwungen — der
      // naechste Dialog-Aufruf faengt die Aenderung.
      await chainRef.current;
      const res = await apiMutate<{ letter: string; aiUsed: boolean; regenerated?: boolean; previousLetter?: string | null }>(
        `/api/events/${id}/offers/generate-letter`,
        "POST",
        { ifStale: true, currentLetter: letter },
      );
      if (res.regenerated !== false) {
        setLetter(res.letter);
        setLetterErzeugt(res.letter);
        setVorherigesAnschreiben(res.previousLetter ?? null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLetterBusy(false);
    }
  }

  /**
   * „Jetzt neu senden“ (Vorgangsseite, sql/770): `?senden=1` öffnet den Senden-Dialog, sobald Vorgang und Varianten
   * geladen sind — einmal, danach verschwindet der Parameter wieder aus der Adresse.
   */
  const [suchParams, setSuchParams] = useSearchParams();
  const sendenWunsch = useRef(suchParams.get("senden") === "1");
  useEffect(() => {
    if (!sendenWunsch.current) return;
    if (eventQ.loading || offersQ.loading || !event || options.length === 0 || !letterHydriert) return;
    sendenWunsch.current = false;
    setSuchParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.delete("senden");
        return n;
      },
      { replace: true },
    );
    void openSend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventQ.loading, offersQ.loading, event, options.length, letterHydriert]);

  /**
   * Das Angebot als PDF-Datei herunterladen.
   *
   * Der Aufbau laeuft auf dem Server (dieselbe Datenquelle wie die Druckansicht) und kann bei
   * einem langen Angebot ein, zwei Sekunden dauern — deshalb sagt der Knopf waehrenddessen, dass
   * etwas passiert. Ohne diese Rueckmeldung klickt ein Betreiber zweimal und bekommt zwei Dateien.
   */
  async function ladePdf() {
    if (!activeId) return;
    setPdfLaedt(true);
    try {
      await ladeDateiHerunter(`/api/offers/${activeId}/document.pdf`, "Angebot.pdf");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPdfLaedt(false);
    }
  }

  /** Druckfertiges HTML (E-Mail-Vorschau / Angebotsdokument) in neuem Tab oeffnen. */
  async function preview(path: string) {
    try {
      await openPrintableDocument(path);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  /** "Gültig bis" direkt aus dem Senden-Dialog pflegen (events.offer_valid_until). */
  /** V-1304: Vorschlag aus „Datum liegt in der Vergangenheit“ übernehmen (Sende-Dialog). */
  async function patchEventDate(iso: string, isoEnde?: string): Promise<boolean> {
    if (!id) return false;
    try {
      // Mehrtägig: Start und Ende in EINEM PATCH, sonst lehnt event-patch.ts „Start nach Ende“ ab.
      await apiMutate(`/api/events/${id}`, "PATCH", isoEnde ? { eventDate: iso, dateEnd: isoEnde } : { eventDate: iso });
      eventQ.reload();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    }
  }

  async function patchValidUntil(value: string) {
    if (!id) return;
    try {
      await apiMutate(`/api/events/${id}`, "PATCH", { offerValidUntil: value || null });
      eventQ.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function regenerateLetter(excludedOptionIds: string[] = []) {
    if (!id) return;
    setVorherigesAnschreiben(null);
    setLetterBusy(true);
    try {
      const res = await apiMutate<{ letter: string }>(`/api/events/${id}/offers/generate-letter`, "POST", excludedOptionIds.length > 0 ? { excludedOptionIds } : {});
      setLetter(res.letter);
      setLetterErzeugt(res.letter);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLetterBusy(false);
    }
  }

  /** Punkt 7: "Text ueberarbeiten" - LEICHTE Korrektur des VORHANDENEN Textes (Rechtschreibung,
   *  kleine Formulierung), keine Neugenerierung. Server persistiert bei Erfolg selbst
   *  (`refine-letter`, wie `generate-letter`) - kein zusaetzlicher PATCH noetig. */
  async function refineLetter() {
    if (!id || !letter.trim()) return;
    setLetterRefineBusy(true);
    try {
      const res = await apiMutate<{ letter: string }>(`/api/events/${id}/offers/refine-letter`, "POST", { text: letter });
      setLetter(res.letter);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLetterRefineBusy(false);
    }
  }

  /** Anschreiben speichern (Edits) und Angebot senden. Bei Erfolg -> 5b (Erfolgs-Ansicht).
   *  Punkt 9 (§ 3.2/§ 3.3): verzweigt intern nach `payload.includeOffer` -- ist die Checkbox
   *  "Aktuelles Angebot als Link und PDF anfuegen" aus, ruft der Freitext-Zweig eine eigene,
   *  bewusst schlanke Route ohne Options-/Preis-/Snapshot-Logik; der Varianten-Zweig (heutiges
   *  Verhalten) bleibt unveraendert unten stehen.
   *
   *  Schritt 8 (Konzept § 4.8/§ 6.5, Einheit C): bleibt beim Parent -- fachlicher State
   *  (`save()`-Flush H-09/H-10, `offer-letter`-PATCH H-13, `offersQ.reload()`, `id`), exakt
   *  dasselbe Muster wie `applyPackage`/`onApply` (Schritt 6) und `parseFreeform`/
   *  `onParseFreeform` (Schritt 7). Die SIGNATUR wurde umgebaut, weil `ccSelf`/`includeOffer`/
   *  `freetextText` jetzt lokaler State von `AngebotSendenDialog` sind: statt sie per Closure zu
   *  lesen, kommen sie als `payload` herein; statt `sendPhase`/`sentResult`/`sendMailError`/
   *  `freetextSentAt` selbst zu setzen, gibt die Funktion das Ergebnis zurueck
   *  (`SendOutcome | null`) -- `AngebotSendenDialog.handleSend()` uebernimmt es in ihren lokalen
   *  State. `subject`/`letter` bleiben Parent-State (unveraendert per Closure gelesen). Die
   *  REIHENFOLGE flush -> Anschreiben-Save -> Versand-API-Call -> Auswertung ist BYTE-IDENTISCH
   *  zum vorherigen Stand, nur Ein-/Ausgabe wurden von Closures auf Parameter/Rueckgabewert
   *  umgestellt -- kein Schritt wurde umsortiert, entfernt oder ergaenzt. */
  async function sendOffer(payload: SendPayload): Promise<SendOutcome | null> {
    if (!id) return null;
    setError(null);
    try {
      if (!payload.includeOffer) {
        // Bewusst KEIN Save-Flush (H-09/H-10) und KEIN Anschreiben-Save (H-13) -- die haengen an
        // `offer_options`/`offer-letter`, die es im Freitext-Zweig nicht gibt (§ 3.3 woertlich).
        const text = payload.freetextText.trim();
        if (!text) {
          setError("NICHT versendet: Bitte einen Text eingeben.");
          return null;
        }
        const trimmedSubject = subject.trim();
        if (!trimmedSubject) {
          setError("NICHT versendet: Bitte einen Betreff eingeben.");
          return null;
        }
        const data = await apiMutate<{ emailSent: boolean; emailError: string | null; emailMessageId: string | null }>(
          `/api/events/${id}/offers/send-freetext`,
          "POST",
          { subject: trimmedSubject, text, ccSelf: payload.ccSelf },
        );
        if (!data.emailSent) {
          // Wie beim Varianten-Pfad: bei Zustellfehler NICHT in die Erfolgsphase wechseln.
          return { phase: "mail_failed", mailError: data.emailError ?? "unbekannter Fehler" };
        }
        activityQ.reload();
        return { phase: "sent", mailError: null, freetextSentAt: new Date().toISOString() };
      }

      // Varianten-Pfad (heutiges Verhalten, unveraendert):
      // M-O: ausstehenden Debounce-Autosave zuerst flushen, sonst geht der letzte Edit
      // (Positionen/Rabatt) beim Versand verloren.
      // H-09: Versand NUR bei bestaetigtem Serverstand. save() faengt Konflikt (409) und
      // Fehler intern - vorher loeste das Promise normal auf und der Kunde bekam die alte
      // Serverfassung, waehrend der Betreiber seine letzten Aenderungen in der UI sah.
      // H-10: save() wartet dank Queue auf saemtliche laufenden UND nachgeschobenen Saves -
      // ein waehrend eines laufenden Saves sofort aufloesendes Promise gibt es nicht mehr.
      if (versandGesperrt) {
        setError(`NICHT versendet: ${ANGEBOT_GESPERRT_KURZ}`);
        return null;
      }
      letzterSaveGesperrtRef.current = false;
      const saveOutcome = await save();
      if (saveOutcome !== "saved") {
        setError(
          letzterSaveGesperrtRef.current
            ? `NICHT versendet: ${ANGEBOT_GESPERRT_KURZ}`
            : saveOutcome === "conflict"
            ? "NICHT versendet: Die Variante wurde zwischenzeitlich anderweitig geändert (Konflikt). Bitte neu laden, prüfen und erneut senden."
            : "NICHT versendet: Der letzte Stand konnte nicht gespeichert werden. Bitte Fehler beheben und erneut senden.",
        );
        return null;
      }
      /**
       * B3 (UX-Polish 25.08.2026, selektiver Versand): Die im Dialog abgewaehlten Varianten
       * werden JETZT deaktiviert — nach dem Save-Flush, VOR dem Versand. Der Server stellt das
       * versendbare Angebot aus den dann noch aktiven Varianten zusammen
       * (`routes/offers.ts`, `POST /api/events/:id/offers/send`), es braucht also keinen neuen
       * Parameter an der Versandroute und keine zweite Auswahl-Logik dort.
       *
       * DAS NADELOEHR WIRD RESPEKTIERT, NICHT UMGANGEN: der Body traegt `{isActive:false}` als
       * EINZIGES Feld — genau die Gestalt, die `PATCH /api/offers/:id` auch im gesperrten
       * Zustand durchlaesst (`test/offer-lock.test.ts` § „Rueckweg"). Die beiden anderen
       * Bedingungen (nicht die gewaehlte, nie die letzte aktive) haelt der Dialog schon vor dem
       * Klick ein (`abwahlGesperrtGrund`), damit hier gar nichts erst gegen die Sperre laeuft.
       *
       * SCHEITERT EINE DEAKTIVIERUNG, WIRD NICHT GESENDET. Sonst entstuende genau die
       * Fehlerklasse, vor der `CLAUDE.md` warnt: die Mail geht raus, und die abgewaehlte
       * Variante steht trotzdem auf der Angebotsseite — nicht mehr zurueckholbar, weil die Mail
       * beim Kunden ist. Lieber ein Abbruch mit Begruendung als ein halber Versand.
       */
      if (payload.deaktivierteOptionIds.length > 0) {
        for (const optId of payload.deaktivierteOptionIds) {
          try {
            await apiMutate(`/api/offers/${optId}`, "PATCH", { isActive: false });
          } catch (e) {
            setError(
              `NICHT versendet: Eine abgewählte Variante konnte nicht deaktiviert werden (${e instanceof Error ? e.message : String(e)}). ` +
                `Bitte die Auswahl prüfen und erneut senden.`,
            );
            offersQ.reload(); // schon deaktivierte Varianten sollen nicht als aktiv stehenbleiben
            return null;
          }
        }
        // War die gerade angezeigte Variante dabei, waehlt der Init-Effekt nach dem Reload die
        // erste uebrige — dasselbe Vorgehen wie in `deleteOption`.
        if (activeId && payload.deaktivierteOptionIds.includes(activeId)) setActiveId(null);
      }

      if (letter.trim() && !nachtragEntwurf) {
        // H-13: Anschreiben-Save ist zwingende Vorbedingung. Vorher verschluckte
        // .catch(() => {}) den Fehler und das Angebot ging mit dem ALTEN serverseitigen
        // Anschreiben raus, waehrend der Betreiber seine Edits in der UI sah.
        try {
          await apiMutate(`/api/events/${id}/offer-letter`, "PATCH", { letter });
        } catch (e) {
          setError(
            `NICHT versendet: Das Anschreiben konnte nicht gespeichert werden (${e instanceof Error ? e.message : String(e)}). Bitte erneut versuchen.`,
          );
          return null;
        }
      }
      const data = await apiMutate<{ publicToken: string; version: number; emailSent: boolean; emailError: string | null }>(
        `/api/events/${id}/offers/send`,
        "POST",
        {
          subject: subject.trim() || undefined,
          ccSelf: payload.ccSelf,
          ...(payload.trotzdem ? { trotzdem: true } : {}),
          ...(payload.datumBestaetigt ? { datumBestaetigt: true } : {}),
        },
      );
      const sentResult = { publicToken: data.publicToken, version: data.version, sentAt: new Date().toISOString() };
      offersQ.reload();
      activityQ.reload(); // Statuskarte „In Änderung“: Versand sofort als Schritt 3 zeigen
      // Nachtrag: das Banner wechselt auf „versendet – wartet auf Zustimmung“ (events.nachtrag_version).
      if (nachtragEntwurf) eventQ.reload();
      if (!data.emailSent) {
        // H-13: NICHT in die Erfolgsphase wechseln - das Angebot ist versioniert und der
        // Link gueltig, aber die Kundin hat KEINE E-Mail bekommen.
        return { phase: "mail_failed", mailError: data.emailError ?? "unbekannter Fehler", sentResult };
      }
      return { phase: "sent", mailError: null, sentResult };
    } catch (e) {
      // Neuversand-Schutz (20.10.2026): 409 `adresse_unzustellbar` ist kein Fehler fuer die Seite,
      // sondern fuehrt in den Dialog „Erneut senden?“ (AngebotSendenDialog) — darum weiterreichen.
      if (alsUnzustellbar(e)) throw e;
      // Migration 731: 422 `a_la_carte_mit_betrag` verständlich (eine Variante trägt das Kennzeichen,
      // hat aber einen Betrag).
      setError(aLaCarteFehlertext(e) ?? (e instanceof Error ? e.message : String(e)));
      return null;
    }
  }

  /**
   * Punkt 8 (Bughunt 27.08.2026, Antoine): "wenn ich eine Nachricht mit einem neuen Angebot an
   * einen Gast schicke, wuerde ich auch gerne die Kostenuebernahme integrieren koennen."
   * KEIN Zusammenlegen der Mails/Vorlagen - die Kostenuebernahme ist ein rechtlich relevanter,
   * eigenstaendig gehaerteter Versandweg (39 Tests, CAS-Schutz, Waisen-Vertrag-Kompensation,
   * `apps/api/src/routes/cost-acceptance.ts`) und bleibt UNVERAENDERT. Dieser Handler ruft nur
   * denselben, bereits bestehenden Endpunkt auf, den auch `abrechnung.tsx` nutzt
   * (`requestCostAcceptance()`) - EIN zusaetzlicher, unabhaengiger Request nach dem Angebots-
   * Versand, ausgeloest durch einen Haken im Sende-Dialog. Schlaegt er fehl, bleibt das bereits
   * gesendete Angebot unangetastet (kein Rollback) - der Fehler wird sichtbar zurueckgegeben,
   * nicht verschluckt, und laesst sich auf der Abrechnungsseite nachholen.
   */
  async function requestCostAcceptanceFromSend(personalNote: string, kundentyp?: Kundentyp): Promise<{ ok: true } | { ok: false; message: string }> {
    if (!id) return { ok: false, message: "Kein Vorgang geladen." };
    try {
      const note = personalNote.trim();
      // Punkt 42: bestaetigter Kundentyp aus dem Dialog (Server speichert ihn am Kunden).
      await apiMutate(`/api/events/${id}/cost-acceptance`, "POST", { ...(note ? { personalNote: note } : {}), ...(kundentyp ? { kundentyp } : {}) });
      if (kundentyp) customerQ.reload();
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e instanceof Error ? e.message : String(e) };
    }
  }

  /* -------------------------------- Ableitungen ------------------------------ */
  const guestCountByDay = useMemo(() => {
    const m: Record<number, number> = {};
    for (const d of days) m[d.dayNo] = d.guestCount;
    return m;
  }, [days]);

  // KONZEPT-GESAMTPREIS-PRO-MENUE, verallgemeinert (Bughunt 27.08.2026): dieselbe
  // sectionPricing-Eingabe wie serverseitig recompute() — sonst zeigte die Live-Vorschau
  // die Positions-Summe, waehrend der Server den Gesamtpreis spart. Menue bleibt auf den
  // eigenen Options-Feldern, andere Sektionen (aktuell: drinks) kommen aus `option.sectionPricing`.
  // In EINER useMemo gehalten, damit `breakdown` UND `sectionSubtotals` exakt dieselbe Eingabe
  // sehen (Bughunt 28.08.2026, "Getraenkepauschale 2500x 37.500 EUR statt 50x 750 EUR": zwei
  // getrennt nachgebaute Kollaps-Regeln liefen auseinander, sobald eine dritte Sektion neben
  // `menu` `mode:"total"` bekam — hier kann das nicht mehr passieren, es gibt nur noch EINE Map).
  const combinedSectionPricing = useMemo(
    () => ({
      menu: { mode: option?.pricingMode ?? "per_course", totalPricePerPersonCents: option?.menuTotalPriceCents ?? null },
      ...option?.sectionPricing,
    }),
    [option],
  );

  // Lokale, optimistische Summen (Server bestaetigt beim Autosave).
  const engineInput = useMemo(
    () => ({
      items: toEngineItems(items),
      optionGuestCount: option?.guestCount ?? event?.guestCount ?? 0,
      guestCountByDay,
      discountPercentBp: option?.discountPercentBp ?? null,
      discountAmountCents: option?.discountAmountCents ?? null,
      sectionPricing: combinedSectionPricing,
      wahlMengengenau: option?.wahlMengengenau === true,
    }),
    [items, option, event, guestCountByDay, combinedSectionPricing],
  );
  const breakdown = useMemo(() => computeBreakdown(engineInput), [engineInput]);
  // Punkt R: „davon Speisen / davon Getränke" je Paket-Kopfzeile — dieselbe Engine-Funktion, die
  // auch die USt-Zeilen bildet; `ausPaket` = am Paket hinterlegt statt geschätzt.
  const paketAufteilung = useMemo(() => {
    const m = new Map<string, { teile: PauschalTeil[]; ausPaket: boolean }>();
    for (const it of engineInput.items) {
      const teile = paketTeile(engineInput, it);
      if (teile && it.id) m.set(it.id, { teile, ausPaket: it.drinksShareCents != null });
    }
    return m;
  }, [engineInput]);

  /**
   * Zwischensummen je Sektion fuer die Zusammenfassung (brutto, ohne Rabatt/Anpassung).
   *
   * NIMMT DIE ZAHL AUS DER ENGINE, statt sie zu bilden — `breakdown.sectionTotals`. Hier stand
   * bis zum 17.09.2026 eine eigene Schleife ueber `lineTotals`, und die war trotz des
   * gegenteiligen Kommentars ein zweiter Rechenweg: sie addierte OPTIONALE Positionen mit.
   *
   * Die Engine weist optionale Zeilen in `lineTotals` mit ihrem vollen Betrag aus — das ist
   * richtig, der Kunde soll sehen, was eine Zubuchung kostet — und nimmt sie zugleich aus der
   * Summe heraus (H-21: "reine Anzeige-Zeilen, kein Beitrag zu Summe/Rabatt/USt"). Wer roh
   * aufsummiert, kennt diese Regel nicht und zaehlt sie mit.
   *
   * Gemeldet vom Betreiber (ueber Antoine, 17.09.2026): "Der gesamte Preis stimmt nicht
   * ueberein. Auf die rechte Seite im Zusammenfassungsfenster ist richtig, auf der linke Seite
   * in Fenster Paket Deluxe Event ist falsch." Nachgerechnet am Vorgang Ivan Kokorov: eine
   * optionale Position ueber 250,00 EUR stand im Abschnitt, aber nicht in der Gesamtsumme.
   *
   * Der zweite Grund, warum es hier nicht mehr gerechnet wird, ist die frueher noetige
   * Gaestezahl: die Schleife multiplizierte den Sektions-Gesamtpreis mit einem eigenen
   * `fallback`, waehrend die Engine `optionGuestCount` benutzt. Zwei Wege zur selben Zahl sind
   * zwei Wege, sie falsch zu bekommen.
   */
  const sectionSubtotals = useMemo(() => {
    const cents = new Map(breakdown.sectionTotals.map((st) => [st.section, st.grossCents]));
    return SECTIONS.map((s) => ({ key: s.key, label: s.label, cents: cents.get(s.key) ?? 0 })).filter((s) => s.cents > 0);
  }, [breakdown]);

  /**
   * Builder v3 / B-3: Bruttosumme EINER Sektion - dieselbe Rechnung wie `sectionSubtotals`,
   * nur einzeln abrufbar fuer die Kopfzeile. Kein zweiter Rechenweg: beide nutzen `breakdown`
   * aus derselben `computeBreakdown()`-Engine wie der Server, damit Kopfzeile und Zusammenfassung
   * nie auseinanderlaufen koennen.
   */
  const sektionsSumme = useCallback(
    (section: OfferSection) => sectionSubtotals.find((s) => s.key === section)?.cents ?? 0,
    [sectionSubtotals],
  );

  /**
   * Der USt-Satz einer Sektion. Enthaelt sie mehrere (Speise 7 % neben Getraenk 19 %), wird das
   * gesagt statt einen Satz zu behaupten - eine falsche Prozentzahl im Kopf waere schlimmer als
   * keine.
   */
  const sektionsUst = useCallback((secItems: OfferItem[]) => {
    const saetze = [...new Set(secItems.map((it) => it.vatRateBp))];
    if (saetze.length === 0) return "";
    if (saetze.length > 1) return "gemischte USt";
    return `${vatLabel(saetze[0]!)} USt`;
  }, []);

  /** Positionen ohne Tageszuordnung — auf einer mehrtaegigen Variante brauchen sie einen Reiter
   *  (siehe `OHNE_TAG`), sonst sind sie in KEINEM zu sehen und zaehlen trotzdem in die Summe. */
  const ohneTagAnzahl = items.filter((it) => it.section !== "adjustment" && it.dayNo == null).length;
  /** Der Reiter bleibt stehen, solange er offen ist — sonst zeigte die Seite nach dem Leeren
   *  der Gruppe eine leere Liste ohne erkennbaren Grund. */
  const zeigeOhneTagReiter = days.length > 0 && (ohneTagAnzahl > 0 || activeDay === OHNE_TAG);
  const tagesFilterAktiv = days.length > 0 && activeDay != null;

  const visibleItems = items.filter((it) => {
    if (it.section === "adjustment") return false;
    if (!tagesFilterAktiv) return true;
    return activeDay === OHNE_TAG ? it.dayNo == null : it.dayNo === activeDay;
  });
  const adjustment = items.find((it) => it.section === "adjustment");
  /**
   * Mockup v3: welche Positionen stecken im Paketpreis? Bestandteile eines Pakets (`packageId`,
   * Katalogbezug, 0,00 €, nicht zubuchbar), die `ungedeckteNullpreisIds` NICHT als Preisluecke
   * meldet — also genau die, deren Paket-Kopfzeile in der Variante steht. Dieselbe Regel wie der
   * Versand-Riegel, kein zweites Urteil. Ueber ALLE Positionen der Variante (auch anderer Tage),
   * sonst fehlte womoeglich die deckende Kopfzeile.
   */
  const imPaketIds = useMemo(() => {
    const luecken = ungedeckteNullpreisIds(items);
    return new Set(
      // 12.10.2026 (Antoine, „Gala Silvester Menü"): KEIN Katalogbezug verlangt — Freitext-Gänge
      // eines Pakets sind genauso im Paketpreis enthalten und standen sonst mit „à 0,00 €" da.
      // Die Kopfzeile selbst fällt über `unitPriceCents === 0` heraus (sie trägt den Preis).
      items.filter((it) => it.packageId != null && it.unitPriceCents === 0 && !it.isOptional && !luecken.has(it.id)).map((it) => it.id),
    );
  }, [items]);
  /**
   * Mockup v3, Prüfzeile „Allergene offen": Speisen und Getränke, deren LMIV-Angabe fehlt —
   * dieselbe Bedingung, unter der die Zeile „Allergene fehlen" zeigt (ItemRow) und die
   * Fußzeile „Allergene unvollständig" sagt (`aggregateAllergens`). Die Paket-Kopfzeile ist
   * kein Gericht und zählt nicht.
   */
  const abgelehnteVorschlaege = useAbgelehnteVorschlaege();
  const allergenLuecken = useMemo(
    () =>
      items.filter((it) => {
        if (it.section !== "menu" && it.section !== "drinks") return false;
        if (it.packageId != null && it.catalogItemId == null && it.unitPriceCents > 0) return false;
        const l = it.catalogItemId ? lmivByCatalogId.get(it.catalogItemId) : undefined;
        return !l || l.allergens == null || (l.allergens.length === 0 && l.allergensCheckedAt == null);
      }),
    [items, lmivByCatalogId],
  );

  /*
   * Hier stand bis zum 17.09.2026 `ohnePreisIds` — die Menge der Positionen, die MAESTRO als
   * „ohne Preis" anschrie. Sie ist mit den Warnungen entfallen (Antoine: „nimm alle diese
   * Warnungen weg"). Die Regel selbst lebt weiter, dort wo sie hingehört: im Katalog-Import
   * (§5.7), wo eine Position ohne Preis und ohne Vermerk eine echte Pflegelücke ist.
   *
   * Sie hier stehen zu lassen und nur die Anzeige zu entfernen wäre die schlechtere Hälfte
   * gewesen: eine Größe, die gerechnet und durchgereicht wird, ohne je etwas zu bewirken, sieht
   * für den Nächsten aus wie ein Mechanismus, der wirkt.
   */

  /**
   * R34-4 (Sende-Reife-Bündel, Konzept § 2/§ 4): vier bereits vorhandene Signale, hier fuer den
   * CTA-Zaehler gebuendelt (bis B1 zusaetzlich fuer die entfallene Karte „Bereit zum Senden").
   * Kein zweiter Rechenweg — jedes Signal ist ein bereits vorhandener Wert bzw. eine bereits an
   * anderer Stelle etablierte Bedingung, hier nur zusammengefasst:
   *  - `menuPreisFehlt`: EXAKT dieselbe Bedingung wie in `PositionsListe.tsx` (R34-1/R34-3,
   *    dort auf Sektionsebene mit `key === "menu"` zusaetzlich gefiltert — da es je Option
   *    genau eine Menue-Sektion gibt, s. `PositionsListe.tsx`-Doku, ist die Bedingung ohne den
   *    Sektions-Schluessel hier aequivalent), gespiegelt auf demselben `option`-Objekt.
   *  - `offeneRueckfragenCount === 0`: der Rueckkanal-Wert von `VorDemSendenKarte.tsx` (s.o.).
   *  - `offeneWuensche.length === 0`: derselbe Array wie oben.
   */
  // sql/445: beide Pauschal-Modi brauchen einen Betrag, sonst zaehlt das Menue mit 0,00 €. Das
  // ist KEINE Preiswarnung im Sinne vom 17.09.2026 (die sind alle weg), sondern eine echte
  // Luecke: ein Modus ist gewaehlt, der Betrag dazu fehlt.
  const menuPreisFehlt = istPauschal(option?.pricingMode) && option?.menuTotalPriceCents == null;
  /** Mockup 34b: jede offene Zeile bekommt ein Sprungziel zur Stelle, die sie abhaken wuerde;
   *  jede erledigte Zeile (wo sinnvoll) den Wert, der sie erledigt hat. Kein neuer Rechenweg —
   *  nur bereits vorhandene Werte/Mechanismen (setOpenSection, vorDemSendenRef) zusammengefuehrt. */
  // Migration 731: eine „À la carte“-Variante hat bewusst keinen Betrag - der Punkt „Menüpreis
  // eingetragen“ wird dann nicht verlangt (und damit auch nicht als offen gezählt).
  const aLaCarteAktiv = istALaCarteOption(option);
  const sendeReifeItems = useMemo(() => {
    const zuVorDemSenden = () => vorDemSendenRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    return [
      ...(aLaCarteAktiv
        ? []
        : [
      {
        key: "menupreis",
        label: "Menüpreis eingetragen",
        done: !menuPreisFehlt,
        // Die Einheit muss am Betrag stehen: „6.500,00 €/Person" waere bei einer Raumpauschale
        // die falsche Auskunft an genau der Stelle, an der der Betreiber sie fuer bare Muenze nimmt.
        wert:
          istPauschal(option?.pricingMode) && option?.menuTotalPriceCents != null
            ? `${money(option.menuTotalPriceCents)}${option.pricingMode === "total_event" ? " gesamt" : "/Person"}`
            : undefined,
        aktion: menuPreisFehlt ? { label: "Festlegen", onClick: () => setOpenSection("menu") } : undefined,
      },
          ]),
      // „Alle Positionen bepreist" stand hier bis zum 17.09.2026. Es war keine Sende-Reife,
      // sondern eine Behauptung darüber, wie ein Angebot auszusehen hat — und im Normalfall
      // (Menüpreis über fünf Gänge) dauerhaft rot, obwohl alles richtig war.
      {
        key: "rueckfragen",
        label: "Rückfragen beantwortet",
        done: offeneRueckfragenCount === 0,
        aktion: offeneRueckfragenCount > 0 ? { label: "Öffnen", onClick: zuVorDemSenden } : undefined,
      },
      {
        key: "wuensche",
        label: "Wünsche geklärt",
        done: offeneWuensche.length === 0,
        aktion: offeneWuensche.length > 0 ? { label: "Öffnen", onClick: zuVorDemSenden } : undefined,
      },
    ];
  }, [menuPreisFehlt, aLaCarteAktiv, offeneRueckfragenCount, offeneWuensche, option]);
  const sendeReifeOffen = sendeReifeItems.filter((i) => !i.done).length;
  /**
   * B1: Die Namen der offenen Punkte. Bis zum 25.08.2026 zeigte sie die Karte „Bereit zum
   * Senden"; mit ihr faellt sonst genau die Information weg, die zaehlt. „2 Punkte offen" ist
   * eine Zahl, „Rückfragen beantwortet, Wünsche geklärt" ist eine Handlungsanweisung — und der
   * Unterschied entscheidet darueber, ob ein unfertiges Angebot rausgeht. Dieselbe Liste
   * versorgt den CTA-Tooltip, die Zeile darunter und den Senden-Dialog; kein zweiter Rechenweg.
   */
  const offenePunkte = useMemo(() => sendeReifeItems.filter((i) => !i.done).map((i) => i.label), [sendeReifeItems]);
  /**
   * Abhak-Storyboard (Konzept § 2/§ 4, "nur der einmalige Puls-Ring am CTA"): der CTA soll genau
   * EINMAL pulsen, wenn `sendeReifeOffen` von >0 auf 0 wechselt — kein Loop/keine Wiederholung.
   * `sendeReifeErreichtRef` haelt fest, ob der aktuelle "fertig"-Zustand schon gepulst hat;
   * sinkt `sendeReifeOffen` wieder unter 0 (z.B. eine neue Position ohne Preis) und erreicht
   * danach erneut 0, ist das ein neuer, echter "gerade fertig geworden"-Moment und pulst wieder.
   */
  const sendeReifeErreichtRef = useRef(false);
  const [ctaPulseAktiv, setCtaPulseAktiv] = useState(false);
  useEffect(() => {
    if (sendeReifeOffen > 0) {
      sendeReifeErreichtRef.current = false;
      return;
    }
    if (sendeReifeErreichtRef.current) return;
    sendeReifeErreichtRef.current = true;
    setCtaPulseAktiv(true);
    const timer = setTimeout(() => setCtaPulseAktiv(false), 700);
    return () => clearTimeout(timer);
  }, [sendeReifeOffen]);

  const fallbackGuests = option?.guestCount ?? event?.guestCount ?? 0;
  /** Gaestezahl fuer eine Zeile: Tagesbezug schlaegt Options-Default (spiegelt die Engine-Logik
   *  guestsFor() aus packages/pricing -- fuer die Live-Vorschau in ItemRow). */
  function guestsForItem(it: OfferItem): number {
    if (it.dayNo != null && it.dayNo in guestCountByDay) return guestCountByDay[it.dayNo]!;
    return fallbackGuests;
  }

  // Schritt 7 (Phase B, Konzept § 2.9): die Katalog-Filterung (Suche + Karten-Filter) ist nach
  // `ui/PositionHinzufuegenDrawer.tsx` gewandert -- sie haengt an `catalogSearch`/
  // `catalogCardFilter`, die jetzt dort lokal sind (analog zu `offeneRueckfragen` in Schritt 5,
  // das mit `beantworteteFragen` mitziehen musste).

  // Schritt 12 Phase B (Konzept § 3, Einheit F): die Live-Vorschau-Zeile fuer den Preis-Regler
  // (8d, vormals discountPreviewLine) ist mit discountMode/discountPreviewRaw nach
  // ui/ZusammenfassungsKarte.tsx gewandert -- sie haengt ausschliesslich an lokalem UI-State
  // dieser Karte plus breakdown.subtotalCents (bereits Prop), kein zweiter Rechenweg.

  // Mockup v3: die Prüfliste „MAESTRO hat geprüft" — nur Regeln über vorhandene Daten (Kopf von
  // ui/PruefSpalte.tsx). Die Tagesprüfung liest dieselbe Route wie die Karte „Angefragter Tag".
  const tagesPruefung = useTagesPruefung(id, {
    onDatumFestlegen: () => setEckdatenOffen(true),
    onTagAnsehen: () =>
      document.querySelector('[data-testid="builder-angefragte-tage"]')?.scrollIntoView({ behavior: "smooth", block: "start" }),
  });
  const pruefungen: Pruefung[] = [];
  const mitKartenVorschlag = editable
    ? allergenLuecken.filter((it) => !abgelehnteVorschlaege.has(it.id) && kartenVorschlag(it, catalogQ.data ?? [])).length
    : 0;
  if (catalogQ.data && items.some((it) => it.section === "menu" || it.section === "drinks")) {
    pruefungen.push(
      allergenLuecken.length === 0
        ? { key: "allergene", ok: true, titel: "Allergene vollständig", text: "Alle Speisen und Getränke haben gepflegte Angaben." }
        : {
            key: "allergene",
            ok: false,
            titel: "Allergene offen",
            text:
              "Fehlt bei: " +
              allergenLuecken
                .slice(0, 3)
                .map((it) => it.name || "ohne Bezeichnung")
                .join(", ") + (allergenLuecken.length > 3 ? ` und ${allergenLuecken.length - 3} weitere` : "") +
              // „Aus Karte übernehmen?" (Mockup 13.10.2026): dieselbe Regel wie der Vorschlag in der Zeile.
              (mitKartenVorschlag > 0 ? ` · davon ${mitKartenVorschlag} mit Vorschlag aus der Karte` : ""),
            aktion: {
              label: "Zeigen",
              onClick: () => {
                setMobilReiter("angebot");
                setTimeout(
                  () => document.querySelector(`[data-testid="builder-item-row-${allergenLuecken[0]!.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }),
                  50,
                );
              },
            },
          },
    );
  }
  if (tagesPruefung) pruefungen.push(tagesPruefung);
  const gaestePruefung = gaestezahlPruefung(event?.guestCount, option?.guestCount, () => setEckdatenOffen(true));
  if (gaestePruefung) pruefungen.push(gaestePruefung);
  if (customerQ.data) {
    const adresse = pruefeRechnungsadresse({
      ...customerQ.data,
      eventAddressStreet: event?.eventAddressStreet,
      eventAddressZip: event?.eventAddressZip,
      eventAddressCity: event?.eventAddressCity,
    });
    pruefungen.push({
      key: "rechnungsadresse",
      ok: adresse.fehlt.length === 0,
      titel: adresse.fehlt.length === 0 ? "Rechnungsadresse vollständig" : "Rechnungsadresse unvollständig",
      text: adresse.fehlt.length === 0 ? adresse.zeile : `Fehlt: ${adresse.fehlt.join(", ")}`,
      aktion: { label: adresse.fehlt.length === 0 ? "Ansehen" : "Ergänzen", onClick: () => setEckdatenOffen(true) },
    });
  }
  const hinweisAnzahl = sendeReifeOffen + pruefungen.filter((p) => !p.ok).length;
  const kundeName = customerQ.data?.name ?? "Kunde";
  const zurKonditionen = () => {
    setMobilReiter("angebot");
    setOffenerReiter("zahlung");
    setTimeout(() => nachschlagenRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };


  if (!id) return <ErrorNote message="Kein Event angegeben." />;
  if (eventQ.loading || offersQ.loading) return <LoadingRows rows={6} />;
  if (eventQ.error) return <ErrorNote message={eventQ.error} />;

  // M3-Loeschdialog: keine Buchstaben-Kennung mehr (Antoine, 11.09.2026) - Titel oder Preis
  // benennen die zu loeschende Variante.
  const zuLoeschendeOption = deleteOptionId ? options.find((o) => o.id === deleteOptionId) : undefined;
  const offeneOption = options.find((o) => o.id === activeId);
  const offeneVarianteName = offeneOption ? offeneOption.title || `Variante ${offeneOption.label}` : null;
  const deleteOptionBezeichnung = zuLoeschendeOption ? zuLoeschendeOption.title || (zuLoeschendeOption.aLaCarte ? NACH_KARTE : money(zuLoeschendeOption.amountTotalCents)) : undefined;

  return (
    <>
      <PageHeader
        /* Mockup v3: „Angebot für <Kunde>" — der Name verlinkt ins Kundenprofil (vorher in der
           Meta-Zeile). Der Rückweg bleibt „<Vorgang> › Angebot". */
        title={
          <span data-testid="builder-titel">
            Angebot für{" "}
            {event?.customerId ? (
              <Link to={`/kunden/${event.customerId}`} className="underline-offset-4 hover:underline" title="Kundenprofil öffnen">
                {kundeName}
              </Link>
            ) : (
              kundeName
            )}
          </span>
        }
        aktuell="Angebot"
        // Punkt 115 (G3): „<Vorgang> › Angebot“ statt des frueheren Knopfs „← Zurück“.
        zurueck={{ label: anlassAnzeige(event?.occasion) ?? "Vorgang", to: vorgangPfad(id) }}
        subtitle={
          /* Status, dann EIN Knopf fuer die Eckdaten (Rueckbau der Inline-Eckdaten 01.09.2026:
             das Fenster ist der Bearbeiten-Modus, `ui/VeranstaltungsDatenDrawer.tsx`). Die
             Kommunikationssprache steht als eigene Pille daneben. */
          event ? (
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {EVENT_STATUS[event.status] && <StatusPill meta={EVENT_STATUS[event.status]} />}
              {/* Antoine 28.09.2026 (Freigabe v3): ist „Nicht zum Lernen verwenden" an, steht das
                  als dezente Pille neben dem Status. Ein Klick oeffnet das ⋯-Menue und setzt den
                  Fokus auf den Schalter — dort wird es wieder ausgeschaltet. */}
              {event.lernenAusgeschlossen === true && (
                <button
                  type="button"
                  data-testid="builder-lern-pille"
                  title="Dieses Angebot dient MAESTRO nie als Beispiel. Klicken zum Ändern."
                  className="rounded-full"
                  onClick={() => {
                    const menue = document.querySelector<HTMLButtonElement>('[data-testid="builder-mehr-menue"]');
                    if (menue?.getAttribute("aria-expanded") !== "true") menue?.click();
                    setTimeout(() => document.querySelector<HTMLElement>('[data-testid="lern-schalter"] [role="switch"]')?.focus(), 0);
                  }}
                >
                  <StatusPill meta={{ label: "Nicht zum Lernen", tone: "neutral" }} icon="psychology" />
                </button>
              )}
              <button
                type="button"
                data-testid="builder-eckdaten-oeffnen"
                onClick={() => setEckdatenOffen(true)}
                title="Datum, Gästezahl, Uhrzeit, Adresse und Tagesplanung ändern"
                className="inline-flex items-center gap-1 rounded-md px-1 text-left underline-offset-2 hover:bg-surface-container-high/60 hover:underline dark:hover:bg-surface-variant/25"
              >
                <span>
                  {[
                    formatDate(event.eventDate),
                    event.guestCount != null ? `${event.guestCount} Gäste` : null,
                    event.timeFrom ? `${event.timeFrom.slice(0, 5)} Uhr` : null,
                    anlassAnzeige(event.occasion),
                    event.location,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                <Icon name="edit" className="shrink-0 text-[13px] opacity-50" />
              </button>
              {/* Punkt 41 (Antoine 26.09.2026): die Kommunikationssprache gut sichtbar und direkt
                  umschaltbar — in der Eckdaten-Zeile wie im Mockup v3 (dieselbe Pille wie auf der
                  Vorgangsseite). Zusätzlich im „⋯"-Menü unter „Einstellungen". */}
              <SprachWahl
                darstellung="pille"
                eventId={event.id}
                eventNumber={event.number}
                sprache={event.offerLanguage}
                quelle={event.offerLanguageQuelle}
                kundeName={customerQ.data?.company || customerQ.data?.name}
                onGeaendert={() => {
                  eventQ.reload();
                  customerQ.reload();
                }}
              />
            </span>
          ) : undefined
        }
        actions={
          <>
            {/* A6: der Autosave-Zustand steht fest im Kopf — samt Ausweg bei Konflikt/Fehler. */}
            {options.length > 0 && (
              <SpeicherStatus
                saveState={saveState}
                errorMessage={saveErrorMessage}
                onReloadConflict={() => activeId && void loadFull(activeId)}
                onErneutSpeichern={() => void save()}
              />
            )}
            {/* Mockup v3: das „⋯"-Menü. Hier stehen, was vorher gleichrangig im Kopf stand
                (Kundenseite, PDF, Löschen — rot, nur solange nichts beim Kunden war) und die
                Einstellungen dieses Angebots (Kommunikationssprache + beim Kunden merken, Lernen).
                Die Zahlungs-Konditionen bleiben ein Chip über dem Dokument (Antoine). */}
            {/* z-40: das offene Menue liegt ueber der festen Summenleiste (z-30) am Handy. */}
            <div className="relative z-40">
            <Aufklapper
              className="w-[340px] max-h-[70vh] overflow-y-auto"
              ausloeser={({ offen, umschalten }) => (
                <Button
                  type="button"
                  variant="secondary"
                  className="!px-2.5 dark:border-outline/40 dark:text-secondary-fixed-dim dark:hover:bg-white/[0.08]"
                  aria-label="Weitere Aktionen"
                  aria-expanded={offen}
                  aria-haspopup="menu"
                  data-testid="builder-mehr-menue"
                  onClick={umschalten}
                >
                  <Icon name="more_horiz" className="text-[18px]" />
                </Button>
              )}
            >
              {(schliessen) => (
                <>
                  <MenueTitel>Einstellungen</MenueTitel>
                  {event && (
                    <SprachWahl
                      darstellung="menue"
                      eventId={event.id}
                      eventNumber={event.number}
                      sprache={event.offerLanguage}
                      quelle={event.offerLanguageQuelle}
                      kundeName={customerQ.data?.company || customerQ.data?.name}
                      onGeaendert={() => {
                        eventQ.reload();
                        customerQ.reload();
                      }}
                    />
                  )}
                  {id && event && event.lernenAusgeschlossen != null && (
                    <LernSchalter eventId={id} ausgeschlossen={event.lernenAusgeschlossen} onGeaendert={() => eventQ.reload()} />
                  )}
                  <div className="my-1 border-t border-outline-variant/30 dark:border-outline/30" />
                  <MenueEintrag
                    icon="public"
                    title="So sieht der Gast die Angebotsseite — auch vor dem Versand"
                    onClick={() => {
                      schliessen();
                      window.open(angebotsseitePfad(id), "_blank", "noopener");
                    }}
                  >
                    Kundenseite ansehen
                  </MenueEintrag>
                  {activeId && (
                    <MenueEintrag
                      icon="download"
                      disabled={pdfLaedt}
                      onClick={() => {
                        schliessen();
                        void ladePdf();
                      }}
                    >
                      {pdfLaedt ? "PDF wird erstellt …" : "Als PDF herunterladen"}
                    </MenueEintrag>
                  )}
                  {/* Loeschen nur, solange nichts beim Kunden war (Antoines Regel 31.07.2026). */}
                  {entwurfLoeschbar && (
                    <>
                      <div className="my-1 border-t border-outline-variant/30 dark:border-outline/30" />
                      <MenueEintrag
                        icon="delete"
                        ton="gefahr"
                        className="dark:!text-[#e39b92]"
                        onClick={() => {
                          schliessen();
                          setLoeschenOffen(true);
                        }}
                      >
                        Entwurf löschen
                      </MenueEintrag>
                    </>
                  )}
                </>
              )}
            </Aufklapper>
            </div>
          </>
        }
      />

      {/* KÜ V2 (S1): im Änderungsmodus steht auch im Builder, welche Kostenübernahme weiter gilt. */}
      {event && id && <AenderungOffenBanner status={event.status} aktivitaeten={activityQ.data ?? []} eventId={id} kundenName={customerQ.data?.company || customerQ.data?.name} />}

      {/* Nachtrag E5: was der automatische Ausgleich nach der Zustimmung mit den Zahlungen gemacht hat (oder noch offen ist). */}
      {event && id && (
        <NachtragAusgleichBanner eventId={id} details={event.details} version={event.acceptedOfferVersion ?? null} istInhaber={istInhaber} onVeraendert={() => eventQ.reload()} />
      )}

      {/* Sperre nach verbindlicher Annahme: vorher sagen statt erst beim Senden scheitern. */}
      {event && id && !nachtragEntwurf && (
        <AngebotGesperrtHinweis
          status={event.status}
          eventId={id}
          version={event.acceptedOfferVersion ?? null}
          betragCents={event.amountTotalCents}
          istInhaber={istInhaber}
          onErstellt={(optionId) => {
            // Der Entwurf existiert serverseitig, `options` kennt ihn erst nach dem Reload: dorthin wechseln, sobald er da ist.
            if (optionId) pendingActivateIdRef.current = optionId;
            offersQ.reload();
            eventQ.reload();
          }}
          onFreigeschaltet={() => {
            offersQ.reload();
            eventQ.reload();
          }}
        />
      )}
      {event && id && nachtragEntwurf && (
        <NachtragLaeuft
          eventId={id}
          gilt={event.acceptedOfferVersion ?? null}
          geltendCents={event.amountTotalCents}
          naechsteVersion={Math.max(event.currentOfferVersion ?? 0, event.acceptedOfferVersion ?? 0) + 1}
          gesendetVersion={event.nachtragVersion ?? null}
          gesendetAm={event.nachtragSeit}
          entwurf={nachtragEntwurf}
          istInhaber={istInhaber}
          refreshKey={`${nachtragEntwurf.id}:${option?.itemsVersion ?? 0}:${saveState}:${event.nachtragVersion ?? ""}`}
          eventGuestCount={event.guestCount}
          eventDate={event.eventDate}
          onSenden={() => void openSend()}
          onVeraendert={() => {
            offersQ.reload();
            eventQ.reload();
            setActiveId(null);
          }}
        />
      )}

      {error && (istMenuewahlMeldung(error) ? <MenuewahlMeldung text={error} /> : <FehlerOderHinweis message={error} onNeuSenden={() => void openSend()} />)}

      <VeranstaltungsDatenDrawer
        open={eckdatenOffen}
        onClose={() => setEckdatenOffen(false)}
        eventId={id}
        eventDate={event?.eventDate}
        eventGuestCount={event?.guestCount}
        eventTimeFrom={event?.timeFrom}
        eventTimeTo={event?.timeTo}
        eventAddressStreet={event?.eventAddressStreet}
        eventAddressZip={event?.eventAddressZip}
        eventAddressCity={event?.eventAddressCity}
        eventAddressCompany={event?.eventAddressCompany}
        eventAddressName={event?.eventAddressName}
        eventAddressAddition={event?.eventAddressAddition}
        eventAddressCountryCode={event?.eventAddressCountryCode}
        deliveryAddressStreet={event?.deliveryAddressStreet}
        deliveryAddressZip={event?.deliveryAddressZip}
        deliveryAddressCity={event?.deliveryAddressCity}
        deliveryAddressCompany={event?.deliveryAddressCompany}
        deliveryAddressName={event?.deliveryAddressName}
        deliveryAddressAddition={event?.deliveryAddressAddition}
        deliveryAddressCountryCode={event?.deliveryAddressCountryCode}
        customerAddressStreet={customerQ.data?.addressStreet}
        customerAddressZip={customerQ.data?.addressZip}
        customerAddressCity={customerQ.data?.addressCity}
        customerCompany={customerQ.data?.company}
        customerName={customerQ.data?.name}
        customerAddressAddition={customerQ.data?.addressAddition}
        customerAddressCountryCode={customerQ.data?.addressCountryCode}
        onEventChanged={() => {
          eventQ.reload();
          // Gästezahl: der Server zieht alle Varianten mit und rechnet neu (lib/event-patch.ts).
          void offersQ.reload();
          if (activeId && (saveState === "idle" || saveState === "saved")) void loadFull(activeId);
        }}
        option={option}
        activeId={activeId}
        onOptionChange={setOption}
        save={save}
        setError={setError}
        editable={editable}
        days={days}
        activeDay={activeDay}
        onSelectDay={setActiveDay}
        zeigeOhneTagReiter={zeigeOhneTagReiter}
        ohneTagAnzahl={ohneTagAnzahl}
        onEnableDays={enableDays}
        onAddDay={addDay}
        onPatchDay={patchDay}
        onRequestDeleteDay={setDeleteDayNo}
      />
      {options.length === 0 ? (
        <>
          <BetriebGeschlossenHinweis lookup={placesQ.data ?? null} firma={customerQ.data?.company ?? null} />
          <ReferenzAnalyseHinweis />
          <Card>
            <EmptyState icon="description" title="Noch keine Angebotsvariante" hint="Legen Sie die erste Variante an." />
            <div className="flex flex-wrap justify-center gap-2.5 pb-8">
              <Button type="button" onClick={() => void addOption()} disabled={busyOption}>
                <Icon name="add" className="text-[18px]" /> Variante anlegen
              </Button>
              {/* „Altes Angebot übernehmen“ — dasselbe Fenster wie in der Chip-Leiste. */}
              <Button
                type="button"
                variant="secondary"
                className="dark:border-outline/40 dark:text-secondary-fixed-dim dark:hover:bg-white/[0.08]"
                onClick={() => setAltangebotOffen(true)}
                disabled={busyOption}
              >
                <Icon name="content_copy" className="text-[18px]" /> Altes Angebot übernehmen
              </Button>
            </div>
          </Card>
        </>
      ) : (
        <>
          {/* Mockup v3 (Handy/Tablet unter lg): zwei Reiter statt drei — die Anfrage steht im
              Chip „Anfrage im Original" über dem Dokument (Antoines Freigabe 28.09.2026). */}
          <div className="-mx-4 mb-4 lg:hidden" data-testid="builder-mobil-reiter">
            <ListenReiter
              label="Angebot oder Prüfen"
              reiter={[
                { key: "angebot", label: "Angebot", anzahl: items.filter((it) => it.section !== "adjustment").length },
                { key: "pruefen", label: "Prüfen", anzahl: hinweisAnzahl },
              ]}
              aktiv={mobilReiter}
              onWahl={setMobilReiter}
            />
          </div>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
            {/* -------- Dokument -------- */}
            <div className={`${mobilReiter === "angebot" ? "" : "hidden"} min-w-0 space-y-4 lg:block`} data-testid="builder-dokument">
              <VariantenLeiste
                options={options}
                activeId={activeId}
                onSelect={setActiveId}
                option={option}
                breakdown={breakdown}
                editable={editable}
                busyOption={busyOption}
                kiFuellenSeit={kiFuellenSeit}
                kiWartetMs={kiWartetMs}
                entwurfLoeschbar={entwurfLoeschbar}
                itemCount={items.length}
                addOption={addOption}
                duplicateOption={duplicateOption}
                varianteMitKiFuellen={varianteMitKiFuellen}
                setDeleteOptionId={setDeleteOptionId}
                angebotGesperrt={angebotGesperrt}
                angebotVersendet={event?.status === "offer_sent"}
                fassungGesperrt={event?.offerFassungVeraltetSeit != null}
                onALaCarteGeaendert={(positionenEntfernt) => {
                  // sql/770: der Sperr-Hinweis kommt vom Server; Option 2: Positionen/Summe/If-Match neu einlesen.
                  eventQ.reload();
                  if (positionenEntfernt && activeId) void loadFull(activeId);
                }}
                onOptionChange={setOption}
                onTitelGespeichert={() => offersQ.reload()}
                setError={setError}
              />

              {/* H-12: waehrend eines Variantenwechsels oder nach fehlgeschlagenem Laden ist der
                  Editor gesperrt - Skeleton statt (potenziell alter) Inhalte unter der neuen ID. */}
              {!geladen && (
                <Card className="p-4">
                  <LoadingRows rows={4} />
                </Card>
              )}
              {geladen && nurLesen && event && (
                <AngenommeneFassungNurLesen
                  eventId={event.id}
                  version={event.acceptedOfferVersion ?? null}
                  entwurfVorhanden={nachtragEntwurf != null}
                  onZumEntwurf={() => nachtragEntwurf && setActiveId(nachtragEntwurf.id)}
                />
              )}

              {/* Die Chip-Leiste bleibt wie heute (Antoines Freigabe 28.09.2026: statt der linken
                  Spalte „Anfrage" aus dem Mockup) — Anfrage im Original, Lesart, offene Punkte,
                  Pakete, Zahlungs-Konditionen. Immer nur EIN Reiter offen (Regel 03.08.2026). */}
              <div ref={nachschlagenRef} className="scroll-mt-20">
                <NachschlagenTabs
                  customerNotes={event?.customerNotes}
                  inquiryLanguage={event?.inquiryLanguage}
                  inquiryTranslation={event?.inquiryTranslation}
                  verstaendnis={option?.aiMeta?.verstaendnis}
                  komposition={komposition}
                  variantenGesamt={options.length}
                  onOffenenPunktAusblenden={geladen ? offenenPunktAusblenden : undefined}
                  editable={editable}
                  setError={setError}
                  packages={packagesQ.data ?? []}
                  packagesLoading={packagesQ.loading}
                  onApplyPackage={applyPackage}
                  onAltesAngebot={() => setAltangebotOffen(true)}
                  offenerReiter={offenerReiter}
                  onSetOffenerReiter={setOffenerReiter}
                  eventId={id}
                  offerValidUntil={event?.offerValidUntil}
                  onPatchValidUntil={patchValidUntil}
                  paymentTermsLoading={paymentTermsQ.loading}
                  paymentTermsData={paymentTermsQ.data}
                  zahlungswege={zahlungswegeQ.data ?? null}
                  ptOverride={ptOverride}
                  ptLoadedFor={ptLoadedFor}
                  ptSaving={ptSaving}
                  ptError={ptError}
                  onSavePaymentTerms={savePaymentTerms}
                  aLaCarte={aLaCarteAktiv}
                  andereBepreist={options.some((o) => o.id !== activeId && !istALaCarteOption(o))}
                />
              </div>

              {/* Lernen S1/S2: Begruendung „Orientiert an …“. Der Schalter „Nicht zum Lernen
                  verwenden" steht seit Mockup v3 im „⋯"-Menü (`LernSchalter`). */}
              {id && option ? (
                <LernHinweis
                  eventId={id}
                  orientiertAn={option.aiMeta?.orientiertAn}
                  angewendet={option.aiMeta?.angewendet}
                  rundung={option.aiMeta?.rundung}
                  ausgeschlossen={event?.lernenAusgeschlossen ?? null}
                  onGeaendert={() => eventQ.reload()}
                  schalterAusblenden
                />
              ) : null}

              {/* Mockup v3: Tage-Reiter über dem Dokument, nur bei mehreren Tagen (oder wenn
                  Positionen ohne Tag aufzufangen sind). Dieselben Werte wie die Tagesplanung
                  im Eckdaten-Fenster (`activeDay`, `OHNE_TAG`). */}
              {geladen && (days.length > 1 || zeigeOhneTagReiter) && (
                <Card className="overflow-hidden !p-0" data-testid="builder-tage-reiter">
                  <ListenReiter
                    label="Tage der Veranstaltung"
                    reiter={[
                      ...days.map((d) => ({
                        key: String(d.dayNo),
                        label: d.label ?? `Tag ${d.dayNo + 1}`,
                        anzahl: items.filter((it) => it.section !== "adjustment" && it.dayNo === d.dayNo).length,
                      })),
                      ...(zeigeOhneTagReiter ? [{ key: String(OHNE_TAG), label: "Ohne Tag", anzahl: ohneTagAnzahl }] : []),
                    ]}
                    aktiv={String(activeDay ?? "")}
                    onWahl={(k) => setActiveDay(Number(k))}
                  />
                </Card>
              )}

              {/* sql/771: Menüwahl im Paket - lesender Kasten über den Positionen (Menüs, Art, Aufpreis, Gäste). */}
              {geladen && <MenuewahlKasten gruppen={menuewahlGruppen(items, guestsForItem)} />}

              {/* Schritt 11: Sektionen (Region 4c). Mockup v3: Paket-Kopf, „im Paket",
                  Werkzeuge beim Darüberfahren, Einfügelinie zwischen den Gängen. */}
              <PositionsListe
                editable={editable}
                nurLesen={geladen && nurLesen}
                visibleItems={visibleItems}
                catalogItems={catalogQ.data ?? []}
                sektionsSumme={sektionsSumme}
                sektionsUst={sektionsUst}
                guestsForItem={guestsForItem}
                guestCount={fallbackGuests}
                lmivByCatalogId={lmivByCatalogId}
                openSection={openSection}
                onSelectOpenSection={setOpenSection}
                option={option}
                onSetPricingMode={setPricingMode}
                onSetMenuTotalPrice={setMenuTotalPrice}
                onSetSectionPricing={setSectionPricing}
                setError={setError}
                bearbeiteteAbschnitte={bearbeiteteAbschnitte}
                onAbschnittBearbeiten={abschnittBearbeiten}
                openPositionDrawer={openPositionDrawer}
                patchItem={patchItem}
                removeItem={removeItem}
                onDragEnd={handleDragEnd}
                imPaketIds={imPaketIds}
                paketAufteilung={paketAufteilung}
                onPaketUebernehmen={
                  editable && (packagesQ.data ?? []).some((pk) => pk.archivedAt == null)
                    ? () => {
                        setOffenerReiter("pakete");
                        setTimeout(() => nachschlagenRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
                      }
                    : undefined
                }
              />

              {/* Mockup v3: der Fuß des Dokuments, so wie der Gast ihn sieht. */}
              {geladen && option && (
                <DokumentFuss
                  aLaCarte={aLaCarteAktiv}
                  zahlung={paymentTermsQ.data?.summary}
                  gueltigBis={event?.offerValidUntil}
                  onKonditionen={zurKonditionen}
                  sectionSubtotals={sectionSubtotals}
                  breakdown={breakdown}
                  gaeste={fallbackGuests}
                />
              )}

              {/* Punkt 76: die offenen WUENSCHE des Gastes (preislose Positionen) — Sprungziel
                  der Zeile „Wünsche geklärt" rechts. */}
              <div ref={vorDemSendenRef}>
                <VorDemSendenKarte offeneWuensche={offeneWuensche} />
              </div>
            </div>

            {/* -------- Prüfen & senden -------- */}
            <div
              className={`${mobilReiter === "pruefen" ? "" : "hidden"} space-y-4 lg:block lg:px-1 lg:pb-2`}
              data-testid="builder-pruefspalte"
            >
              {/* R4.2 und Referenz-Analyse: Hinweise nur, wenn sie zutreffen. */}
              <BetriebGeschlossenHinweis lookup={placesQ.data ?? null} firma={customerQ.data?.company ?? null} />
              <ReferenzAnalyseHinweis />
              {/* Karte „Angefragter Tag" (Punkt 56/78: Kalenderblatt + Wochenstreifen aus dem
                  Kalender) wieder offen ganz oben — Mockup v3 (#1471) hatte sie hinter die
                  zugeklappte Zeile „Tag" gelegt; Antoine 15.10.2026: „Es soll genauso aussehen
                  wie vorher." */}
              {id && <AngefragteTageKarte eventId={id} onDatumFestlegen={() => setEckdatenOffen(true)} />}
              <ZusammenfassungsKarte
                option={option}
                sectionSubtotals={sectionSubtotals}
                breakdown={breakdown}
                activeId={activeId}
                adjustment={adjustment}
                setDiscount={setDiscount}
                setTargetPrice={setTargetPrice}
                setError={setError}
              />

              <BereitZumSendenKarte punkte={sendeReifeItems} pruefungen={pruefungen}>
                {/* Der EINE primaere Knopf (Mockup v3). Er OEFFNET nur den Senden-Dialog
                    (Antoine 03.08.2026: „An Kunden senden" war missverstaendlich) — die
                    Beschriftung nennt offene Punkte, die Farbe bleibt immer `btn-primary`
                    (Antoine 20.09.2026: „rot und gleich sichtbar"). Unter lg traegt die feste
                    Leiste unten denselben Knopf, deshalb hier `hidden lg:block`. */}
                <div className="hidden space-y-2 lg:block">
                  <button
                    type="button"
                    data-testid="builder-senden-cta-desktop"
                    className={`w-full btn-primary ${ctaPulseAktiv ? "maestro-cta-pulse" : ""}`}
                    disabled={!geladen}
                    onClick={() => void openSend()}
                    title={
                      sendeReifeOffen === 0
                        ? "Öffnet Vorschau und Anschreiben — gesendet wird erst im nächsten Schritt"
                        : `Noch offen: ${offenePunkte.join(", ")}. Öffnet Vorschau und Anschreiben — gesendet wird erst im nächsten Schritt`
                    }
                  >
                    <Icon name="send" className="text-[18px]" />{" "}
                    {sendeReifeOffen === 0 ? (nachtragEntwurf ? "Nachtrag senden" : "Angebot senden") : `Senden vorbereiten … — ${sendeReifeOffen} ${sendeReifeOffen === 1 ? "Punkt" : "Punkte"} offen`}
                  </button>
                  <p className="flex items-center justify-center gap-1.5 text-beleg-mikro text-on-surface-variant/70 dark:text-secondary-fixed-dim">
                    <Icon name="lock" className="text-[13px]" /> Als Nächstes siehst du Anschreiben und Vorschau. Nichts geht ohne deine Freigabe raus.
                  </p>
                </div>
                {/* Sekundaer: die Sicht des Gastes und die Datei (Antoine 21.09.2026: PDF statt
                    Druckansicht). */}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    className="!px-2 dark:border-outline/40 dark:text-secondary-fixed-dim dark:hover:bg-white/[0.08]"
                    title="So sieht der Gast die Angebotsseite — auch vor dem Versand"
                    onClick={() => window.open(angebotsseitePfad(id), "_blank", "noopener")}
                  >
                    <Icon name="visibility" className="text-[18px]" /> Vorschau
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="!px-2 dark:border-outline/40 dark:text-secondary-fixed-dim dark:hover:bg-white/[0.08]"
                    disabled={pdfLaedt || !activeId}
                    title="Das Angebot als PDF-Datei herunterladen"
                    onClick={() => void ladePdf()}
                  >
                    <Icon name="download" className="text-[18px]" /> {pdfLaedt ? "PDF …" : "PDF"}
                  </Button>
                </div>
              </BereitZumSendenKarte>

              {/* Mockup v3: „Verlauf" als zuklappbare Zeile (Versionen + Angebots-Verlauf, E8,
                  F-VERSANDSTATUS). Die Karte „Angefragter Tag" steht wieder offen oben. */}
              <AufklappZeile
                icon="history"
                titel="Verlauf"
                wert={event?.offerSentAt ? `gesendet ${formatDate(event.offerSentAt)}` : "noch nicht gesendet"}
                offen={verlaufOffen}
                onUmschalten={() => setVerlaufOffen((v) => !v)}
                testId="builder-zeile-verlauf"
              >
                <VersionenKarte kompakt offerSentAt={event?.offerSentAt} currentOfferVersion={event?.currentOfferVersion} saveState={saveState} />
                {id && (
                  <AngebotsVerlaufKarte
                    eventId={id}
                    onGeaendert={() => {
                      eventQ.reload();
                      offersQ.reload();
                    }}
                  />
                )}
              </AufklappZeile>
              {breakdown.vatBreakdown.length > 0 && (
                <p className="flex items-start gap-2 px-1 text-beleg-mikro text-on-surface-variant/80 dark:text-secondary-fixed-dim">
                  <Icon name="info" className="mt-0.5 shrink-0 text-[15px]" />
                  Alle Preise inkl. USt. Die Aufteilung nach Sätzen steht so auch auf dem Angebot.
                </p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Mobile (unter lg): feste Leiste mit Summe + Senden ueber der App-Navigation (8e,
          Mockup v3 „Hauptaktion"). Abstand, Hoehe und Platzhalter haengen zusammen: MOBIL_UNTEN. */}
      {options.length > 0 && geladen && option && (
        <div className={`fixed inset-x-3 ${MOBIL_UNTEN.leiste.klasse} z-30 flex items-center gap-3 rounded-2xl border border-outline-variant/50 bg-surface-container-lowest p-3 shadow-2xl dark:border-outline/30 dark:bg-night-card lg:hidden`}>
          <div className="min-w-0 flex-1" data-testid="builder-mobil-summe">
            <p className="truncate text-[17px] font-bold text-primary dark:text-primary-fixed-dim">{aLaCarteAktiv ? NACH_KARTE : money(breakdown.amountTotalCents)}</p>
            <p className="truncate text-[11.5px] text-on-surface-variant dark:text-secondary-fixed-dim">
              {aLaCarteAktiv ? `${A_LA_CARTE_VOR_ORT} · ${fallbackGuests} Gäste` : `${money(breakdown.perPersonCents)} pro Gast · ${fallbackGuests} Gäste`}
            </p>
          </div>
          {/* A6: auf schmalem Schirm scrollt der Speicher-Status weg — nur der AUSWEG bleibt hier. */}
          {(saveState === "error" || saveState === "conflict") && (
            <button
              type="button"
              role="alert"
              data-testid="builder-nicht-gespeichert-mobil"
              className="shrink-0 rounded-md bg-error-container px-2.5 py-2 font-label-md text-label-md font-semibold text-error dark:bg-error/25 dark:text-[#e39b92]"
              onClick={() => (saveState === "conflict" ? activeId && void loadFull(activeId) : void save())}
              title={
                saveState === "conflict"
                  ? "Konflikt – neu laden"
                  : saveErrorMessage
                    ? `Nicht gespeichert – ${saveErrorMessage}`
                    : "Nicht gespeichert – erneut versuchen"
              }
            >
              <Icon name={saveState === "conflict" ? "sync_problem" : "cloud_off"} className="text-[18px]" />
            </button>
          )}
          {/* Dieselbe Beschriftungs-Logik und Farbe wie der Knopf rechts (R34-4, 20.09.2026). */}
          <button
            type="button"
            data-testid="builder-senden-cta-mobile"
            className={`shrink-0 btn-primary ${ctaPulseAktiv ? "maestro-cta-pulse" : ""}`}
            onClick={() => void openSend()}
            title="Öffnet Vorschau und Anschreiben — gesendet wird erst im nächsten Schritt"
          >
            <Icon name="send" className="text-[18px]" /> {sendeReifeOffen === 0 ? (nachtragEntwurf ? "Nachtrag senden" : "Angebot senden") : `Senden … (${sendeReifeOffen})`}
          </button>
        </div>
      )}
      {/* Platzhalter, damit die fixierte Summenleiste die letzten Karten nicht ueberdeckt (MOBIL_UNTEN). */}
      {options.length > 0 && <div className={`${MOBIL_UNTEN.platzhalter.klasse} lg:hidden`} />}

      {/* Position hinzufügen (8c) -- EIN Drawer mit 3 Reitern (Katalog/KI-Text/Manuell) statt
          der frueheren drei verstreuten Einstiege. Pakete/Menues sind seit 12.10.2026 (Antoine: „alle
          Kategorien/Pakete/Menüs auswählbar") als Chip im Katalog-Reiter waehlbar - sie laufen
          ueber denselben `applyPackage()` wie der Schnellstart (neue Variante), kein zweiter Weg. */}
      <PositionHinzufuegenDrawer
        open={positionDrawerOpen}
        onClose={() => {
          setPositionDrawerOpen(false);
          einfuegenRef.current = null;
        }}
        section={positionDrawerSection}
        onSectionChange={setPositionDrawerSection}
        catalogItems={catalogQ.data ?? []}
        catalogLoading={catalogQ.loading}
        activeBuilderCards={activeBuilderCards}
        builderCardByCategory={builderCardByCategory}
        builderCardActiveById={builderCardActiveById}
        catalogCategories={builderCatsQ.data ?? []}
        packages={packagesQ.data ?? []}
        onApplyPackage={applyPackage}
        offeneVariante={editable ? offeneVarianteName : null}
        onApplyPackageInAktive={applyPackageInAktive}
        onAddFromCatalog={addFromCatalog}
        onParseFreeform={parseFreeform}
        onAddFreetext={addFreetext}
      />

      {/* Angebot senden — 5a (Compose) / 5b (Erfolg) / mail_failed (H-13). Schritt 8 (Konzept
          § 4.8/§ 6.5, Einheit C): nach ui/AngebotSendenDialog.tsx verschoben -- siehe dortigen
          Doku-Kommentar fuer die genaue State-Aufteilung (was lokal ist, was beim Parent bleibt
          und warum). */}
      <AngebotSendenDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        eventId={id}
        eventOccasion={event?.occasion}
        eventDateIso={event?.eventDate}
        eventDateEndIso={event?.dateEnd}
        eventGuestCount={event?.guestCount}
        eventOfferLanguage={event?.offerLanguage}
        eventNumber={event?.number}
        eventOfferValidUntil={event?.offerValidUntil}
        eventPublicToken={event?.publicToken}
        customerId={event?.customerId}
        customerName={customerQ.data?.name}
        customerEmail={customerQ.data?.email}
        companyEmail={settingsQ.data?.companyEmail}
        options={nachtragEntwurf ? [nachtragEntwurf] : options}
        activeId={nachtragEntwurf ? nachtragEntwurf.id : activeId}
        // Der Dialog nennt fuer die gerade bearbeitete Variante denselben Betrag wie die
        // Zusammenfassung rechts. Ohne diese eine Zahl las er den Stand vom Seitenaufruf —
        // Begruendung an der Verwendungsstelle in AngebotSendenDialog.tsx.
        aktiverBetragCents={breakdown.amountTotalCents}
        error={error}
        subject={subject}
        onSubjectChange={setSubject}
        letter={letter}
        onLetterChange={handleLetterChange}
        letterBusy={letterBusy}
        onRegenerateLetter={(excluded) => regenerateLetter(excluded)}
        letterErzeugt={letterErzeugt}
        vorherigesAnschreiben={vorherigesAnschreiben}
        onVorherigesAnschreibenWiederherstellen={() => {
          if (vorherigesAnschreiben !== null) handleLetterChange(vorherigesAnschreiben);
          setVorherigesAnschreiben(null);
        }}
        letterRefineBusy={letterRefineBusy}
        onRefineLetter={() => void refineLetter()}
        onRequestCostAcceptance={requestCostAcceptanceFromSend}
        kundentypUnbestaetigt={customerQ.data?.kundentypAutomatisch ? (customerQ.data.kundentyp ?? null) : null}
        paymentPathway={paymentTermsQ.data?.effective.paymentPathway ?? null}
        onSend={sendOffer}
        onPreview={(path) => void preview(path)}
        onPatchValidUntil={(value) => void patchValidUntil(value)}
        onPatchEventDate={(iso, ende) => patchEventDate(iso, ende)}
        offenePunkte={offenePunkte}
        angebotGesperrt={versandGesperrt}
        freischaltenKarte={
          event && id ? (
            <AngebotFreischaltenKarte
              status={event.status}
              eventId={id}
              version={event.acceptedOfferVersion ?? null}
              betragCents={event.amountTotalCents}
              istInhaber={istInhaber}
              onErstellt={(optionId) => {
                // Nachtrag-Entwurf: Dialog zu, der Builder wechselt in den Entwurf (eigener Versandweg).
                if (optionId) pendingActivateIdRef.current = optionId;
                setSendOpen(false);
                offersQ.reload();
                eventQ.reload();
              }}
              onFreigeschaltet={() => {
                // Dialog bleibt offen und ist nach dem Neuladen sendebereit.
                offersQ.reload();
                eventQ.reload();
              }}
            />
          ) : null
        }
        nachtrag={Boolean(nachtragEntwurf)}
      />

      {id && (
        <AltesAngebotDrawer
          open={altangebotOffen}
          onClose={() => setAltangebotOffen(false)}
          eventId={id}
          zielOptionId={editable ? activeId : null}
          zielLabel={option?.label ?? null}
          gesperrt={angebotGesperrt}
          sektionsLabel={SEKTIONS_LABEL}
          vorUebernahme={() => (editable ? save() : Promise.resolve())}
          onUebernommen={(e) => void altangebotUebernommen(e)}
        />
      )}

      {/* Bestaetigung vor dem Loeschen des ganzen Vorgangs (destruktiv und endgueltig). */}
      <Modal
        open={loeschenOffen}
        onClose={() => setLoeschenOffen(false)}
        width="440px"
        gefahr
        title="Entwurf löschen?"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setLoeschenOffen(false)} disabled={loeschBusy}>
              Abbrechen
            </Button>
            <Button type="button" variant="danger" disabled={loeschBusy} onClick={() => void vorgangLoeschen()}>
              <Icon name="delete_forever" className="text-[16px]" /> Endgültig löschen
            </Button>
          </>
        }
      >
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          Der Vorgang wird mit allen Varianten und Positionen endgültig entfernt. Das lässt sich nicht rückgängig machen.
          Möglich ist es nur, solange nichts an den Kunden versendet wurde.
        </p>
      </Modal>

      {/* M3: Bestaetigung vor dem Loeschen einer Variante (destruktiv). */}
      <Modal
        open={deleteOptionId != null}
        onClose={() => setDeleteOptionId(null)}
        width="440px"
        title={<>Variante {deleteOptionId ? deleteOptionBezeichnung : undefined} löschen?</>}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setDeleteOptionId(null)} disabled={busyOption}>
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={busyOption}
              onClick={() => deleteOptionId && void deleteOption(deleteOptionId)}
            >
              <Icon name="delete" className="text-[16px]" /> Löschen
            </Button>
          </>
        }
      >
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          {options.length <= 1
            ? "Die Variante wird entfernt. Das Angebot bleibt bestehen – danach legen Sie eine neue Variante an."
            : "Die Variante wird aus dem Angebot entfernt. Die übrigen Varianten bleiben bestehen."}
        </p>
      </Modal>

      {/* §5.8: Bestaetigung vor dem Entfernen eines Tages (destruktiv, analog zum
          Varianten-Loeschen-Modal oben statt sofortiger Ausfuehrung). */}
      <Modal
        open={deleteDayNo != null}
        onClose={() => setDeleteDayNo(null)}
        width="440px"
        title={<>{days.find((d) => d.dayNo === deleteDayNo)?.label ?? `Tag ${(deleteDayNo ?? 0) + 1}`} entfernen?</>}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setDeleteDayNo(null)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => {
                if (deleteDayNo != null) removeDay(deleteDayNo);
                setDeleteDayNo(null);
              }}
            >
              <Icon name="delete" className="text-[16px]" /> Entfernen
            </Button>
          </>
        }
      >
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          Positionen dieses Tages bleiben erhalten und werden tagesunabhängig.
        </p>
      </Modal>

      {/* L4: Undo-Toast nach dem Entfernen einer Position (leichte Alternative zum Bestaetigungs-
          Modal -- die Position kann sofort wiederhergestellt werden). Ueber der mobilen Summenleiste. */}
      {altangebotUndo && (
        <div className={`fixed inset-x-0 ${MOBIL_UNTEN.toast.klasse} z-40 flex justify-center px-4 lg:bottom-6`}>
          <div className="flex items-center gap-3 rounded-xl border border-outline-variant/50 bg-surface-container-lowest px-4 py-2.5 shadow-2xl dark:border-outline/30 dark:bg-night-card">
            <Icon name="content_copy" className="text-[16px] text-on-surface-variant" />
            <span className="text-[12.5px] text-on-background dark:text-inverse-on-surface">Variante durch Angebot aus {altangebotUndo.quellNummer} ersetzt</span>
            <button type="button" data-testid="builder-undo-altangebot" className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-primary hover:underline" onClick={() => void altangebotRueckgaengig()}>
              <Icon name="undo" className="text-[15px]" /> Rückgängig
            </button>
          </div>
        </div>
      )}
      {removedItem && (
        <div className={`fixed inset-x-0 ${MOBIL_UNTEN.toast.klasse} z-40 flex justify-center px-4 lg:bottom-6`}>
          <div className="flex items-center gap-3 rounded-xl border border-outline-variant/50 bg-surface-container-lowest px-4 py-2.5 shadow-2xl dark:border-outline/30 dark:bg-night-card">
            <Icon name="delete" className="text-[16px] text-on-surface-variant" />
            <span className="text-[12.5px] text-on-background dark:text-inverse-on-surface">
              Position „{removedItem.item.name.trim() || "ohne Bezeichnung"}" entfernt
            </span>
            <button type="button" data-testid="builder-undo-remove" className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-primary hover:underline" onClick={undoRemove}>
              <Icon name="undo" className="text-[15px]" /> Rückgängig
            </button>
          </div>
        </div>
      )}
    </>
  );
}

